'use client';

import React, {
  ReactElement,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import * as Sentry from '@sentry/react';
import { FirebaseError } from '@firebase/util';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../../../contexts/auth';
import { useAuthRedirect } from '../../../hooks/useAuthRedirect';
import { ProxyLoginContext } from '../../../contexts/ProxyLoginContext';
import { usePageLayout } from '../../../hooks/usePageLayout';
import { useCompanyId } from '../../../hooks/useCompanyId';
import useSnackbar from '../../../hooks/useSnackbar';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Grid2,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  buttonTextIconLarge,
  gray100,
  gray20,
  gray40,
  gray60,
  gray80,
  primaryButtonBackgroundColor,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
  navy50,
  white,
  white_overlay80,
} from '../../../constants/colors';
import {
  boldMd,
  boldXxl,
  spHeaderTitle,
  regularLg,
  regularMd,
  regularSm,
  regularXl,
  regularXs,
} from '../../../constants/location';
import MetaTitle from '../../utils/MetaTitle';
import SideBar from '../../uiParts/menu/SideBar';
import {
  CloudUploadOutlined,
  DeleteOutline,
  FileDownloadOutlined,
  MoreVert,
  CheckCircleOutline,
  PersonOutline,
} from '@mui/icons-material';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import { type InputUploadType } from '../../../types/input';
import {
  DocumentData,
  DocumentSnapshot,
  Timestamp,
  doc,
  onSnapshot,
  updateDoc,
} from 'firebase/firestore';
import InfiniteScroll from 'react-infinite-scroll-component';
import CustomLoading from '../../uiParts/form/CustomLoading';
import DescriptionIcon from '@mui/icons-material/Description';
import CustomSnackbar from '../../uiParts/form/CustomSnackbar';
import DeleteModal from '../../uiParts/modal/DeleteModal';
import FileExportDialog from '../../uiParts/modal/FileExportDialog';
import dayjs from 'dayjs';
import { exportFile } from '../../../api/firebase/function/exportFileFunction';
import { createFileExportHistory } from '../../../api/firebase/firestore/fileExportHistory/create';
import {
  ocrStatus,
  patternStatus,
  processingStatus,
  registerStatus,
  resultStatus,
  fileProcessingType,
  geminiModel,
  geminiThinkingBudget,
  geminiSamplingParam,
  ocrEngineType,
  structuredEngineType,
  processingStatusLabel,
  exportFileType,
  exportEncoding,
  exportQuoteMode,
  PATTERN_ID_OTHER,
  OCR_UPLOAD_MAX_TOTAL_PAGES,
  OCR_UPLOAD_MAX_PAGES_PER_FILE,
  fileNameColumnWidth,
  fileNameColumnWidthThreshold,
  tableCellHorizontalPaddingTotal,
  ocrColumnWidth,
  isProGeminiModel,
  toAvailableGeminiModel,
  GEMINI_MODEL_DEFAULT,
} from '../../../constants/enum';
import { isPdfFile, getPdfPageCount, calculateTotalPageCount } from '../../../utils/pdfToImage';
import { useTotalPageCount } from '../../../hooks/useTotalPageCount';
import { useUserMonthlyOcrLimit } from '../../../hooks/useUserMonthlyOcrLimit';

import { PatternType, ExportSettingsType } from '../../../types/pattern';
import { inputUploadSchema } from '../../../validation/input';
import {
  ResultType,
  ResultWithUserType,
  PatternResultSearchConditions,
  WatchSearchItemsType,
} from '../../../types/result';
import AiOcrSearchForm from '../../uiParts/parts/AiOcrSearchForm';
import SearchCustomizePanel from '../../uiParts/parts/SearchCustomizePanel';
import DeletedBadge from '../../uiParts/parts/DeletedBadge';
import { bulkUpdateProcessingStatus } from '../../../api/firebase/firestore/result/bulkUpdateProcessingStatus';
import ReviewChip from '../../uiParts/ReviewChip';
import { filterByNeedsReview, isNeedsReviewResult } from '../../../utils/pattern/needsReviewFilter';
import { filterBySubPattern } from '../../../utils/pattern/subPatternFilter';
import SubPatternChip from '../../uiParts/SubPatternChip';
import OcrCreditConsumeConfirmModal from '../../uiParts/modal/OcrCreditConsumeConfirmModal';
import { getSubPatterns } from '../../../api/firebase/firestore/subPattern/read';
import type { SubPatternType } from '../../../types/subPattern';
import { OcrFileNameStatus, OcrStatusIcon } from '../../uiParts/parts/OcrStatusFileInfo';
import { getPatternResultsOneTimePaginated } from '../../../api/firebase/firestore/result/read';
import { getPattern } from '../../../api/firebase/firestore/pattern/read';
import {
  getCompanyForAccount,
  getMonthlyUsageCountByUserId,
} from '../../../api/firebase/firestore/company/read';
import { resolveDefaultAssignee } from '../../../api/firebase/firestore/company/resolveDefaultAssignee';
import { getUsersByCompanyId, getUser } from '../../../api/firebase/firestore/user/read';
import type { UserType } from '../../../types/user';
import AssigneeChangeDialog from '../../uiParts/modal/AssigneeChangeDialog';
import StatusChangeConfirmDialog from '../../uiParts/modal/StatusChangeConfirmDialog';
import UploadModal from '../../uiParts/modal/UploadModal';
import { editResult } from '../../../api/firebase/firestore/result/update';
import { db } from '../../../api/firebase/config';
import { deleteResult } from '../../../api/firebase/firestore/result/delete';
import {
  getCreditRateByPlan,
  getOcrLimitByCompany,
  calculateOcrLimitInfo,
  getUserMonthlyOcrLimit,
  getUserMonthlyOcrLimitMessage,
  getOcrLimitOverMessage,
} from '../../../utils/userUsageLimit';
import { isOperatorStaff } from '../../../utils/permission';
import { getUserErrorMessage } from '../../../modules/error';
import { useDetailMode } from '../../../hooks/useDetailMode';
import { getDisplayName } from '../../../utils/displayName';
import { executeProvisionalOcrUsage } from '../../../utils/provisionalOcrUsage';
import { getOcrCreditRateWithModel } from '../../../constants/plans';
import { PROXY_DISPLAY_NAME, PATTERN_MODEL_SELECTABLE_TOOLTIP } from '../../../constants/labels';
import { useProxyDialogSx } from '../../../hooks/useProxyDialogSx';
import { useUploadDispatch } from '../../../contexts/UploadContext';
import HiStoryIcon from '../../assets/hi-story-icon.png';
import NoAccountIcon from '../../assets/Account circle.svg';
import {
  getPatternResultsForExport,
  getPatternResultsCountByStatus,
  searchPatternResultsByAlgolia,
} from '../../../api/firebase/firestore/result/read';
import {
  generatePatternExportData,
  generateExportColumns,
  buildExportBlob,
  generateFileName,
  formatDateValue,
} from '../../../utils/pattern/fileExportUtils';
import OcrLimitDialog from '../../uiParts/modal/OcrLimitDialog';
import PatternSelectModal from '../../uiParts/modal/PatternSelectModal';
import { handleReOcrPatternResult } from '../../../api/firebase/function/handleReOcrPatternResult';
import { SwapHoriz } from '@mui/icons-material';
import TuneIcon from '@mui/icons-material/Tune';
import { useSearchCustomize } from '../../../hooks/useSearchCustomize';
import { extractEnabledSearchInputs } from '../../../utils/searchParamsBuilder';
import {
  buildResultTabQuery,
  parseResultTabParam,
  RESULT_TAB_PARAM_KEY,
} from '../../../utils/pattern/resultTabParam';
import { assigneeLabels } from '../../../constants/assignee';
import { resolveAssigneeDisplay, type AssigneeOption } from '../../../utils/assigneeDisplay';
import { canViewSharedResource } from '../../../utils/groupAccess';
import {
  SearchFieldConfig,
  searchFieldInputType,
  searchMatchMode,
} from '../../../types/searchCustomize';
import {
  OcrItemKeywordValue,
  OcrItemKeywords,
  normalizeOcrItemKeywords,
  toKeywordList,
} from '../../../utils/search/ocrItemKeywords';
import { resolveChoiceLabel } from '../../../utils/pattern/choiceUtils';

// アップロード制限は constants/enum.ts に集約（メール取り込み経路とも共通の値）
const MAX_TOTAL_PAGES = OCR_UPLOAD_MAX_TOTAL_PAGES;
const MAX_PAGES_PER_FILE = OCR_UPLOAD_MAX_PAGES_PER_FILE;

/** PatternAiOcrページの検索カスタマイズデフォルト設定 */
const SEARCH_CUSTOMIZE_DEFAULT_FIELDS: SearchFieldConfig[] = [
  {
    fieldName: 'search_text',
    label: 'フリーワード',
    inputType: searchFieldInputType.freeText,
    enabled: true,
    matchMode: searchMatchMode.fuzzy,
  },
  {
    fieldName: 'assignee',
    label: assigneeLabels.label,
    inputType: searchFieldInputType.select,
    enabled: true,
    matchMode: searchMatchMode.exact,
  },
  {
    fieldName: 'created_by',
    label: '作成者',
    inputType: searchFieldInputType.select,
    // 作成者の検索ドロップダウンはデフォルト非表示（検索・表示設定でONにできる）
    enabled: false,
    matchMode: searchMatchMode.exact,
    // 作成者列も一覧でデフォルト非表示。ユーザーが「一覧表示」で表示ON/OFFを切替可能
    canToggleList: true,
  },
  {
    fieldName: 'ocr_status',
    label: '読取ステータス',
    inputType: searchFieldInputType.select,
    enabled: true,
    matchMode: searchMatchMode.exact,
  },
  {
    // 要注意の絞り込み（#3262）。読取ステータスと同じ扱いでデフォルト有効
    fieldName: 'needs_review',
    label: '要注意',
    inputType: searchFieldInputType.select,
    enabled: true,
    matchMode: searchMatchMode.exact,
  },
  {
    fieldName: 'files.file_name',
    label: 'ファイル名',
    inputType: searchFieldInputType.text,
    enabled: false,
    matchMode: searchMatchMode.fuzzy,
  },
  {
    fieldName: 'date_range',
    label: '日付範囲',
    inputType: searchFieldInputType.dateRange,
    enabled: true,
    matchMode: searchMatchMode.exact,
  },
];

const PatternAiOcr = (): ReactElement => {
  const { patternId } = useParams<{ patternId: string }>();
  const [loading, setLoading] = useState<boolean>(!!patternId);
  const { errorText, infoText, snackbarOpen, setErrorText, setInfoText, setSnackbarOpen } =
    useSnackbar();
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();
  const location = useLocation();
  const { user, company, isLoading } = useContext(AuthContext);
  const { operatorId, operatorRole } = useContext(ProxyLoginContext);
  const { isDetailView } = useDetailMode();
  const { companyId: effectiveCompanyId, isProxyMode } = useCompanyId();
  const { startUpload } = useUploadDispatch();

  // #2960 検証設定（OCRエンジン・座標・sampling・構造化）を利用できるか。
  // 運営スタッフかつ詳細モードON のときのみ利用できる（#3069）。
  // UploadModal の表示条件と揃え、送信値も同じ判定でガードする。
  const canUseVerificationSettings = isDetailView;
  // アップロードモーダルはファイル数に応じて maxHeight: calc(100vh - 32px) まで伸びるため、
  // 代理モードではバナー分を差し引かないと下端がはみ出す（#3310）
  const proxyDialogSx = useProxyDialogSx({ fullHeight: true });
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();
  const [usedCount, setUsedCount] = useState<number>(0);
  const [baseRemainingCount, setBaseRemainingCount] = useState<number | null>(null); // DB上の固定残り枚数
  const [baseRemainingJpy, setBaseRemainingJpy] = useState<number | null>(null);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false); // submit後のバリデーション無効化フラグ

  // 会社のプランに基づいたOCR利用枚数上限を取得
  const userMaxFiles = getOcrLimitByCompany(company);
  const [pattern, setPattern] = useState<PatternType | null>(null);
  // 子パターン（#2967）。有効な子パターンのみ。絞り込みの選択肢に使う
  const [subPatterns, setSubPatterns] = useState<SubPatternType[]>([]);
  // 子パターンを指定した一括再読み取りのモーダル（#2967）
  const [subPatternReOcrModalOpen, setSubPatternReOcrModalOpen] = useState<boolean>(false);
  const [isCheckingPermission, setIsCheckingPermission] = useState<boolean>(true);

  const {
    fields: customizeFields,
    updateField: updateCustomizeField,
    resetToDefault: resetCustomize,
    getMatchMode,
    isFieldEnabled,
    getFieldInputType,
    saveToFirestore: saveSearchSettings,
  } = useSearchCustomize(SEARCH_CUSTOMIZE_DEFAULT_FIELDS, pattern);

  // 作成者列はデフォルト非表示。検索・表示設定で「一覧表示」をONにしたときのみ表示する
  const showCreatorColumn =
    customizeFields?.some(f => f.fieldName === 'created_by' && f.showInList) ?? false;

  // 一覧に表示するカスタマイズ列（OCR読取項目）
  const visibleOcrColumns = useMemo(
    () => customizeFields?.filter(f => f.isOcrItem && f.showInList) ?? [],
    [customizeFields],
  );

  // #3134: ファイル名列が確保する最小幅。カスタマイズ列が増えたら下限を下げ、
  // 他の列に幅を譲れるようにする（横に余白があるうちは下限を超えて広がる）。
  const fileNameColumnMinWidthPx = useMemo(
    () =>
      visibleOcrColumns.length >= fileNameColumnWidthThreshold.narrow
        ? fileNameColumnWidth.narrow
        : fileNameColumnWidth.wide,
    [visibleOcrColumns.length],
  );

  // ファイル名列（ヘッダー・データ共通）の幅指定。
  // width:100% で余った横幅をすべて吸収し、ファイル名をできるだけ省略せずに見せる。
  // カスタマイズ列が増えて余白がなくなると、上の下限まで縮んで他の列に幅を譲る。
  // maxWidth:0 は「コンテンツ由来の要求幅を0として扱う」ための指定。
  // これがないと table-layout:auto では一番長いファイル名の行に列全体が引き伸ばされてしまう。
  const fileNameColumnSx: SxProps<Theme> = useMemo(
    () => ({
      boxSizing: 'border-box',
      width: '100%',
      minWidth: `${fileNameColumnMinWidthPx}px`,
      maxWidth: 0,
    }),
    [fileNameColumnMinWidthPx],
  );

  // データ取得関連
  const resultLimit = 100;
  const [realtimeData, setRealtimeData] = useState<ResultWithUserType[]>([]); // リアルタイム購読データ
  const [searchData, setSearchData] = useState<ResultWithUserType[]>([]); // 検索用全件データ
  const [lastVisible, setLastVisible] = useState<DocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [showDataFlg, setShowDataFlg] = useState<boolean | null>(null);
  // タブ状態はURLで保持する（ファイル出力履歴から戻ったときに同じタブを表示するため）
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTab, setSelectedTab] = useState<number>(
    parseResultTabParam(searchParams.get(RESULT_TAB_PARAM_KEY)),
  );
  const [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false);
  const [fileExportModalOpen, setFileExportModalOpen] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string>('');
  const [limitDialogOpen, setLimitDialogOpen] = useState<boolean>(false);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState<boolean>(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState<boolean>(false);
  const [reclassifyModalOpen, setReclassifyModalOpen] = useState<boolean>(false);
  const [reclassifyLoading, setReclassifyLoading] = useState<boolean>(false);
  const [singleReclassifyTargetId, setSingleReclassifyTargetId] = useState<string>('');
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuTargetId, setMenuTargetId] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [fileCountOverText] = useState<string>('');
  // 担当者（#2642）
  const [assigneeOptions, setAssigneeOptions] = useState<AssigneeOption[]>([]);
  // 担当者「表示解決」用（#3095）: 共有範囲で絞らない会社の全アクティブ(finished)メンバー。
  // 割り当て候補(assigneeOptions)は共有範囲で絞る(#2927)が、既存担当者の表示は、
  // 共有範囲外になった現役メンバーを「退会済ユーザー」と誤表示しないよう絞らないリストで解決する。
  const [assigneeDisplayOptions, setAssigneeDisplayOptions] = useState<AssigneeOption[]>([]);
  const [selectedAssigneeUid, setSelectedAssigneeUid] = useState<string>('');
  // 担当者変更ダイアログ（単一/一括）
  const [assigneeDialogOpen, setAssigneeDialogOpen] = useState<boolean>(false);
  const [assigneeDialogMode, setAssigneeDialogMode] = useState<'single' | 'bulk'>('single');
  const [assigneeDialogTargets, setAssigneeDialogTargets] = useState<string[]>([]);
  const [assigneeDialogCurrent, setAssigneeDialogCurrent] = useState<string | undefined>(undefined);
  const [assigneeDialogLabel, setAssigneeDialogLabel] = useState<string | undefined>(undefined);
  const hasFetched = useRef(false);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const previousPatternId = useRef<string | undefined>(undefined);
  const isPatternChanging = useRef(false);
  const fetchIdRef = useRef(0); // リクエストIDを管理
  const fetchResultsRef = useRef<
    (
      currentLastVisible: DocumentSnapshot | null,
      searchConditions?: WatchSearchItemsType,
    ) => Promise<void>
  >(() => Promise.resolve());
  const freeWordFetchIdRef = useRef(0); // フリーワード検索リクエストIDを管理
  const [appliedFreeWordSearchKey, setAppliedFreeWordSearchKey] = useState<string>('');

  // ファイル処理タイプの状態管理
  const [processingTypeValue, setProcessingTypeValue] = useState<number>(
    fileProcessingType.pagePerResult,
  );

  // pagePerResultモード用: ファイルごとのページ数キャッシュ（ファイル名 → ページ数）
  const [pdfPageCountCache, setPdfPageCountCache] = useState<Map<string, number>>(new Map());

  // Geminiモデル選択の状態管理
  const [selectedGeminiModel, setSelectedGeminiModel] = useState<number>(GEMINI_MODEL_DEFAULT);
  // パターンにPro使用が許可されているか（一般ユーザーへのモデル選択UI表示判定に使用）
  const [isProAllowedPattern, setIsProAllowedPattern] = useState<boolean>(false);

  // 思考設定の状態管理（Gemini 2.5: thinkingBudget数値, Gemini 3: thinkingLevel文字列）
  // Flash: デフォルト無効、Pro: デフォルト最小限（1,024トークン）
  const [thinkingBudgetValue, setThinkingBudgetValue] = useState<number | null>(
    geminiThinkingBudget.disabled,
  );
  const [thinkingLevelValue, setThinkingLevelValue] = useState<string | null>(null);
  // OCRエンジン切替（#2960 運営スタッフ限定・アップロードごとに選択。デフォルトは Vision AI）
  const [ocrEngine, setOcrEngine] = useState<number>(ocrEngineType.visionAi);
  // 読取パターンに設定されたOCRエンジン（#3264）。
  // 詳細モードOFF（一般ユーザー・代理詳細OFF）のときはこの値をそのまま使う。
  // 画面の ocrEngine は運営が検証用に選び直すためのもので、詳細モードOFFでは触れない
  const [patternOcrEngine, setPatternOcrEngine] = useState<number>(ocrEngineType.visionAi);
  // DI系が設定されたパターンか（#3264）。立っていれば一般ユーザーにもエンジン選択を出す
  const [isOcrEngineSelectablePattern, setIsOcrEngineSelectablePattern] = useState<boolean>(false);
  // Geminiサンプリングパラメータ（#2960 運営スタッフ限定・1回目OCR抽出用）。常に送信される。
  const [geminiTemperature, setGeminiTemperature] = useState<number>(
    geminiSamplingParam.temperatureDefault,
  );
  const [geminiTopP, setGeminiTopP] = useState<number>(geminiSamplingParam.topPDefault);
  // 行の矩形座標を Gemini に渡すか（#2960 方式B・運営スタッフ限定）
  const [includeCoordinates, setIncludeCoordinates] = useState<boolean>(false);
  // 2回目構造化エンジン（#2960・運営スタッフ限定。既定は Gemini 3.1 Flash-Lite・#3375）
  const [structuredEngine, setStructuredEngine] = useState<number>(structuredEngineType.gemini);

  // タブ別件数の管理
  const [processingCount, setProcessingCount] = useState<number | null>(null);
  const [completedCount, setCompletedCount] = useState<number | null>(null);
  const [outputtedCount, setOutputtedCount] = useState<number | null>(null);
  const countUnsubscribeRef = useRef<(() => void)[]>([]);

  // 検索機能（遷移元から検索条件がある場合は復元）
  const [watchSearchItems, setWatchSearchItems] = useState<PatternResultSearchConditions>(() => {
    const stateSearchData = location.state?.searchData;
    if (stateSearchData) {
      return {
        ...stateSearchData,
        startDate: stateSearchData.startDate ? dayjs(stateSearchData.startDate) : null,
        endDate: stateSearchData.endDate ? dayjs(stateSearchData.endDate) : null,
      };
    }
    return {
      fileName: null,
      responsiblePerson: null,
      assignee: null,
      startDate: null,
      endDate: null,
      freeWord: null,
      ocrStatus: null,
    };
  });

  // OCR読取項目の検索キーワード（遷移元から検索条件がある場合は復元）
  const [ocrItemKeywords, setOcrItemKeywords] = useState<OcrItemKeywords>(() =>
    normalizeOcrItemKeywords(location.state?.ocrItemKeywords),
  );

  const handleOcrItemKeywordChange = useCallback(
    (fieldName: string, value: OcrItemKeywordValue): void => {
      setOcrItemKeywords(prev => ({ ...prev, [fieldName]: value }));
    },
    [],
  );

  // 検索カスタマイズで項目を無効化した際に、対応する検索値をクリアする
  const handleUpdateCustomizeField = useCallback(
    (fieldName: string, updates: Partial<SearchFieldConfig>): void => {
      updateCustomizeField(fieldName, updates);

      if (updates.enabled === false) {
        if (fieldName === 'search_text') {
          setWatchSearchItems(prev => ({ ...prev, freeWord: null }));
        } else if (fieldName === 'created_by') {
          setWatchSearchItems(prev => ({ ...prev, responsiblePerson: null }));
        } else if (fieldName === 'date_range') {
          setWatchSearchItems(prev => ({ ...prev, startDate: null, endDate: null }));
        } else if (fieldName === 'needs_review') {
          setWatchSearchItems(prev => ({ ...prev, needsReview: undefined }));
        } else if (fieldName === 'ocr_status') {
          setWatchSearchItems(prev => ({ ...prev, ocrStatus: null }));
        } else if (fieldName === 'files.file_name') {
          setWatchSearchItems(prev => ({ ...prev, fileName: null }));
        } else {
          // OCR読取項目: 該当キーワードを除去
          setOcrItemKeywords(prev => {
            if (!(fieldName in prev)) return prev;
            const next = { ...prev };
            delete next[fieldName];
            return next;
          });
        }
      }
    },
    [updateCustomizeField],
  );

  // 検索カスタマイズ設定の保存
  const [isSavingCustomize, setIsSavingCustomize] = useState(false);
  const handleSaveCustomize = useCallback(async (): Promise<void> => {
    if (!user?.uid) return;
    setIsSavingCustomize(true);
    try {
      const savedSettings = await saveSearchSettings(user.uid);
      setPattern(prev => (prev ? { ...prev, search_settings: savedSettings } : prev));
      setInfoText('検索設定を保存しました');
      setSnackbarOpen(true);
    } catch {
      setErrorText('検索設定の保存に失敗しました');
      setSnackbarOpen(true);
    } finally {
      setIsSavingCustomize(false);
    }
  }, [user?.uid, saveSearchSettings, setInfoText, setErrorText, setSnackbarOpen]);

  // 検索条件を Algolia に投げるための per-field 情報を組み立てる
  // - search_text(フリーワード) / files.file_name は固定 fieldName
  // - OCR項目はカスタマイズで定義された fieldName を使う
  const algoliaSearchSpec = useMemo(() => {
    const trimmedFreeWord =
      isFieldEnabled('search_text') && watchSearchItems.freeWord
        ? watchSearchItems.freeWord.trim()
        : '';

    const fileNameRaw = isFieldEnabled('files.file_name')
      ? watchSearchItems.fileName?.trim() || ''
      : '';
    const fileNameMatchMode =
      getMatchMode('files.file_name') === searchMatchMode.exact ? 'exact' : 'fuzzy';

    const ocrItems: Array<{ fieldId: string; keywords: string[]; matchMode: 'fuzzy' | 'exact' }> =
      [];
    for (const [fieldName, value] of Object.entries(ocrItemKeywords)) {
      const keywords = toKeywordList(value);
      if (keywords.length === 0) continue;
      if (!isFieldEnabled(fieldName)) continue;
      // 数値型フィールド: 結果詳細はカンマ区切りで表示されるが Algolia 側は素値で
      // 格納されているため、検索キーワードからカンマを除去して両者を揃える
      const normalizedKeywords =
        getFieldInputType(fieldName) === searchFieldInputType.number
          ? keywords.map(keyword => keyword.replace(/,/g, ''))
          : keywords;
      ocrItems.push({
        fieldId: fieldName,
        keywords: normalizedKeywords,
        matchMode: getMatchMode(fieldName) === searchMatchMode.exact ? 'exact' : 'fuzzy',
      });
    }

    return {
      freeWord: trimmedFreeWord,
      fileName: fileNameRaw,
      fileNameMatchMode: fileNameMatchMode as 'fuzzy' | 'exact',
      ocrItems,
    };
  }, [
    watchSearchItems.freeWord,
    watchSearchItems.fileName,
    ocrItemKeywords,
    isFieldEnabled,
    getMatchMode,
    getFieldInputType,
  ]);

  const hasActiveSearch = useMemo(
    () =>
      !!algoliaSearchSpec.freeWord ||
      !!algoliaSearchSpec.fileName ||
      algoliaSearchSpec.ocrItems.length > 0,
    [algoliaSearchSpec],
  );

  // Algolia 検索キー: 検索条件が変わるたびに再フェッチをトリガーする
  const searchKey = useMemo(() => {
    if (!hasActiveSearch) return '';
    return JSON.stringify({
      ...algoliaSearchSpec,
      responsiblePerson: watchSearchItems.responsiblePerson,
      assignee: watchSearchItems.assignee,
      startDate: watchSearchItems.startDate ? watchSearchItems.startDate.toISOString() : null,
      endDate: watchSearchItems.endDate ? watchSearchItems.endDate.toISOString() : null,
      ocrStatus: watchSearchItems.ocrStatus,
      selectedTab,
      patternId,
    });
  }, [
    hasActiveSearch,
    algoliaSearchSpec,
    watchSearchItems.responsiblePerson,
    watchSearchItems.assignee,
    watchSearchItems.startDate,
    watchSearchItems.endDate,
    watchSearchItems.ocrStatus,
    selectedTab,
    patternId,
  ]);

  const isSearchRefreshing = searchKey !== '' && searchKey !== appliedFreeWordSearchKey;

  // 一括「要注意以外を確認済にする」（#3262）
  const [bulkCompleteDialogOpen, setBulkCompleteDialogOpen] = useState<boolean>(false);
  const [bulkCompleteProcessing, setBulkCompleteProcessing] = useState<boolean>(false);

  const showData = useMemo(() => {
    const base = ((): typeof realtimeData => {
      if (hasActiveSearch) {
        // Algolia が全条件を適用済み。条件変更直後は古い結果を表示しない
        if (isSearchRefreshing) return [];
        return searchData;
      }
      // 検索条件なしの場合は Firestore リアルタイムデータをそのまま表示
      return realtimeData;
    })();
    // 共有範囲によるアクセス制御フィルタ（#2804 Phase3）
    // 運営・企業管理者は全件、一般メンバーは閲覧可能な結果のみ表示
    return base.filter(d =>
      canViewSharedResource(
        { uid: user?.uid ?? '', role: user?.role, group_ids: user?.group_ids },
        d,
      ),
    );
  }, [
    hasActiveSearch,
    isSearchRefreshing,
    searchData,
    realtimeData,
    user?.uid,
    user?.role,
    user?.group_ids,
  ]);

  /**
   * 一括で確認済にする対象（#3262）。
   *
   * チェックボックスで**ユーザーが選んだもの**を対象にする。
   * 要注意以外をまとめて確認済にしたい場合は、検索の「要注意以外」で
   * 絞り込んでから全選択して使う（設計書 §8.4）。
   */
  const bulkCompleteTargetIds = useMemo(() => Array.from(selectedIds), [selectedIds]);

  // 検索クリア関数
  const handleClearSearch = useCallback((): void => {
    // 検索条件をクリア
    setWatchSearchItems({
      fileName: null,
      responsiblePerson: null,
      assignee: null,
      startDate: null,
      endDate: null,
      freeWord: null,
      ocrStatus: null,
      needsReview: undefined, // 要注意フィルタ（#3262）も解除する
      subPatternId: undefined, // 子パターンフィルタ（#2967）も解除する
    });
    setOcrItemKeywords({});
  }, []);

  // React Hook Form
  const {
    control,
    handleSubmit,
    reset,
    watch,
    trigger,
    setError,
    formState: { errors },
  } = useForm<InputUploadType>({
    resolver: yupResolver(inputUploadSchema),
    mode: 'onChange',
    defaultValues: {
      images: [],
    },
  });

  // 合計ページ数を計算（PDFはページ数分、画像は1ページとしてカウント）
  const watchedImages = watch('images');
  const { totalPageCount, isCalculating } = useTotalPageCount(watchedImages);

  // 個人別 月間OCR上限をリアルタイム監視（#3133）
  // 会社枠と同じくファイル選択時点でインラインのバリデーションエラーを出すために事前購読する。
  const { getLimitMessage: getUserLimitMessage } = useUserMonthlyOcrLimit(
    user?.uid,
    effectiveCompanyId ?? user?.company_id,
    isProxyMode,
  );

  const styles = {
    avatar: {
      height: '24px',
      width: '24px',
    },
    buttonLarge: {
      height: '44px',
      padding: '16px',
      '& .MuiButton-icon': {
        marginRight: '4px',
      },
      '@media screen and (max-width:960px)': {
        width: 'calc(50% - 8px)',
      },
      '@media screen and (max-width:600px)': {
        width: '100%',
      },
    },
    buttonSmall: {
      height: '36px',
      '& .MuiButton-icon': {
        marginRight: '4px',
      },
    },
    headerOutlineButton: {
      height: '30px',
      whiteSpace: 'nowrap',
      minWidth: 'auto',
      backgroundColor: white,
      border: `1px solid ${gray80}`,
      color: gray80,
      '&:hover': {
        backgroundColor: white,
        border: `1px solid ${gray60}`,
        color: gray60,
        opacity: '80%',
      },
    },
    dialog: {
      '& .MuiPaper-root': {
        border: '1px solid' + gray40,
        boxSizing: 'border-box',
        margin: '16px',
        padding: 0,
        width: '480px',
        maxHeight: 'calc(100vh - 32px)',
        display: 'flex',
        flexDirection: 'column',
      },
    },
    dialogCloseButton: {
      height: '32px',
      width: '32px',
      '@media screen and (max-width:960px)': {
        height: '24px',
        width: '24px',
      },
    },
    dialogHeader: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
      padding: '16px 32px 12px',
      flexShrink: 0,
      '@media screen and (max-width:960px)': {
        padding: '16px 16px 8px',
      },
    },
    dialogTitle: {
      ...regularXl,
      '@media screen and (max-width:960px)': {
        ...regularLg,
      },
    },
    headerButtonWrapper: {
      display: 'flex',
      gap: '16px',
      '@media screen and (max-width:960px)': {
        boxSizing: 'border-box',
        position: 'fixed',
        bottom: 0,
        left: 0,
        backgroundColor: white_overlay80,
        padding: '16px 16px 24px',
        width: '100%',
        zIndex: 1,
      },
      '@media screen and (max-width:600px)': {
        flexDirection: 'column',
        gap: '12px',
      },
    },
    headerTitle: {
      ...boldXxl,
      color: gray100,
      '@media screen and (max-width:960px)': {
        ...spHeaderTitle,
      },
    },
    icon24: {
      height: '24px',
      marginLeft: '0px',
      width: '24px',
    },
    icon18: {
      height: '18px',
      marginLeft: '0px',
      width: '18px',
    },
    iconButton: {
      padding: 0,
    },
    tab: {
      alignItems: 'center',
      backgroundColor: gray40,
      borderRadius: '4px',
      cursor: 'pointer',
      display: 'flex',
      height: '24px',
      justifyContent: 'center',
      width: '120px',
      '@media screen and (max-width:960px)': {
        width: 'calc(100% / 3)',
        height: 'auto',
        minHeight: '24px',
        padding: '2px 4px',
        boxSizing: 'border-box',
      },
    },
    tabActive: {
      backgroundColor: gray100,
    },
    tabText: {
      ...regularXs,
      color: gray100,
      '@media screen and (max-width:960px)': {
        fontSize: '11px',
        textAlign: 'center',
      },
    },
    tabTextActive: {
      color: white,
    },
    tabAndButtonWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      margin: '0 32px',
      '@media screen and (max-width:960px)': {
        flexWrap: 'wrap',
        margin: '0 16px',
        width: 'calc(100% - 32px)',
      },
    },
    tabWrapper: {
      display: 'flex',
      gap: '12px',
      '@media screen and (max-width:960px)': {
        width: '100%',
      },
    },
    table: {
      minWidth: '960px',
      overflowX: 'auto',
    },
    tableCellAvatar: {
      alignItems: 'center',
      display: 'flex',
      gap: '8px',
      height: '48px',
      padding: '0 16px',
    },
    tableCellButton: {
      padding: 0,
    },
    tableDataText: {
      ...regularSm,
      color: gray100,
      // 行の高さをホーム画面の処理履歴（listItem の上下 12px）に合わせて上下 padding を確保する
      padding: '12px 16px',
    },
    tableHeadText: {
      ...regularSm,
      color: gray80,
      padding: '0 16px',
    },
    // カスタマイズ表示列のヘッダーラベル（#3134）。
    // 項目名は判読できるよう2行までは折り返し、それを超える場合のみ省略記号にする。
    tableHeadDynamicLabel: {
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: 2,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      overflowWrap: 'anywhere',
    },
    // ヘッダー行は 48px 基準。カスタマイズ表示列のラベルが2行になる場合のみ伸びる（#3134）
    tableHeadRow: {
      height: '48px',
      '& .MuiTableCell-root': {
        paddingTop: '8px',
        paddingBottom: '8px',
      },
    },
    tableDataRow: {
      height: '48px',
      '&:hover': {
        backgroundColor: gray20,
        cursor: 'pointer',
      },
    },
    tableSection: {
      backgroundColor: white,
      border: '1px solid' + gray40,
      borderRadius: '4px',
      boxSizing: 'border-box',
      margin: '0 32px',
      minHeight: '560px',
      padding: '16px 24px 56px',
      width: 'calc(100% - 64px)',
      '@media screen and (max-width:960px)': {
        border: 'none',
        borderRadius: 0,
        margin: 0,
        width: '100%',
      },
    },
    tableWrapper: {
      overflowX: 'auto',
      width: '100%',
      WebkitOverflowScrolling: 'touch',
    },
    dialogButtonWrapper: {
      display: 'flex',
      gap: '16px',
      padding: '12px 32px 16px',
      borderTop: `1px solid ${gray40}`,
      backgroundColor: white,
      flexShrink: 0,
      '@media screen and (max-width:960px)': {
        gap: '8px',
        padding: '8px 16px 16px',
      },
    },
    uploadCancelButton: {
      ...tertiaryButtonBackgroundColor,
      height: '44px',
      width: 'calc(50% - 8px)',
    },
    uploadCancelButtonText: {
      ...boldMd,
      color: gray80,
    },
    uploadSubmitButton: {
      height: '44px',
      width: 'calc(50% - 8px)',
      ...primaryButtonBackgroundColor,
    },
    uploadSubmitButtonText: {
      ...boldMd,
      color: white,
    },
    uploadText: {
      ...regularMd,
      color: gray80,
    },
    uploadFileChip: {
      margin: '0 8px 8px 0',
    },
    icon: {
      width: '56px',
      height: '56px',
      margin: '80px 0 24px 0',
      '@media screen and (max-width: 600px)': {
        margin: '24px 0 24px 0',
      },
    },
    title: {
      whiteSpace: 'pre-wrap',
      mb: 2,
      fontWeight: '500',
      color: gray100,
      fontSize: '22px',
      fontStyle: 'normal',
      lineHeight: '1.4',
      '@media screen and (max-width: 600px)': {
        marginBottom: '12px',
      },
    },
    description: {
      whiteSpace: 'pre-wrap',
      mb: 4,
      ...regularMd,
      '@media screen and (max-width: 600px)': {
        marginBottom: '20px',
      },
    },
    titleAndStatusWrapper: {
      flexGrow: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginRight: '18px',
      '@media screen and (max-width: 960px)': {
        marginRight: '0',
        fontSize: '12px',
      },
    },
    showFileTitleText: {
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      display: 'inline-block',
      verticalAlign: 'middle',
    },
    dialogContent: {
      padding: '0 32px 16px',
      flex: 1,
      overflowY: 'auto',
      '@media screen and (max-width:960px)': {
        padding: '0 16px 12px',
      },
    },
    mainEllipsisText: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '450px',
      display: 'inline-block',
      verticalAlign: 'middle',
    },
  };

  // データ取得関数（resultデータ用）
  const fetchResults = useCallback(
    async (
      currentLastVisible: DocumentSnapshot | null,
      searchConditions?: WatchSearchItemsType,
    ): Promise<void> => {
      if (!patternId || !user) {
        return;
      }

      // 新しいリクエストIDを発行
      fetchIdRef.current += 1;
      const currentFetchId = fetchIdRef.current;

      setLoading(true);
      try {
        // 検索条件が指定されていない場合は現在のwatchSearchItemsを使用
        const conditionsToUse = searchConditions || watchSearchItems;

        // API用にWatchSearchItemsType形式で渡す（無効化されたフィールドはnullにする）
        // 抽出ロジックは extractEnabledSearchInputs に集約 (#2642)
        const enabledInputs = extractEnabledSearchInputs(conditionsToUse, isFieldEnabled);
        const apiSearchConditions = {
          fileName: null,
          responsiblePerson: enabledInputs.responsiblePerson,
          assignee: enabledInputs.assignee,
          startDate: enabledInputs.startDate,
          endDate: enabledInputs.endDate,
          ocrStatus: enabledInputs.ocrStatus,
          // 要注意のみ（#3262）。クライアントサイドで絞る（設計書 §6.4）
          needsReview: enabledInputs.needsReview,
          // 子パターン（#2967）。要注意と同じ経路で絞る
          subPatternId: conditionsToUse.subPatternId,
        };

        // 購読停止
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
        }

        // リアルタイム取得開始
        unsubscribeRef.current = await getPatternResultsOneTimePaginated(
          patternId,
          selectedTab, // processing_statusでフィルタ
          resultLimit,
          currentLastVisible,
          async (results, newLastVisible, isLastPage) => {
            try {
              // このリクエストが最新でない場合は結果を無視
              if (currentFetchId !== fetchIdRef.current) {
                return;
              }

              // resultドキュメントに必要な情報がすべて含まれているため、追加のAPI呼び出し不要
              const updatedResults: ResultWithUserType[] = results.map(data => {
                // まず基本のResultTypeにキャスト
                const resultData = data as unknown as ResultType;
                // その後、追加プロパティを加えてResultWithUserTypeに変換
                // 代理操作の場合は「事務局」とHi-Storyアイコンを表示
                return {
                  ...resultData,
                  showName: getDisplayName(resultData.user_name, resultData.is_proxy),
                  iconUrl: resultData.is_proxy ? HiStoryIcon : resultData.user_icon_url || null,
                } as ResultWithUserType;
              });

              setRealtimeData(prevData =>
                currentLastVisible === null ? updatedResults : [...prevData, ...updatedResults],
              );
              setLastVisible(newLastVisible);
              setHasMore(!isLastPage);
              // 初回読み込み時は結果が空でもshowDataFlgをtrueにして、データ表示領域を表示
              // 追加データ読み込み時は結果がある場合のみ継続表示
              if (currentLastVisible === null) {
                // 初回読み込み時は必ずtrueに設定（0件の場合の表示のため）
                setShowDataFlg(true);
              } else if (updatedResults.length > 0) {
                // 追加読み込み時は結果がある場合のみ継続表示
                setShowDataFlg(true);
              }
            } catch (callbackError) {
              setErrorText('データ処理に失敗しました');
              setSnackbarOpen(true);
            } finally {
              setLoading(false);
            }
          },
          apiSearchConditions,
          isDetailView, // 運営スタッフかつ詳細モード時は削除済み結果も含めて取得
          effectiveCompanyId || user?.company_id, // 検索と同じ company_id 絞り込み（代理ログインなしで他社データが見えないようにする）
          () => {
            // インデックス不足などでリアルタイム取得が失敗した場合、
            // callback が呼ばれず loading が解除されないため、ここで解除しエラー表示する
            if (currentFetchId !== fetchIdRef.current) {
              return;
            }
            setLoading(false);
            setShowDataFlg(true);
            setErrorText('検索条件でデータを取得できませんでした');
            setSnackbarOpen(true);
          },
        );
      } catch (e) {
        let errorMessage = 'データ取得に失敗しました';
        if (e instanceof Error) {
          if (e.message.includes('permission-denied')) {
            errorMessage = 'アクセス権限がありません。ログインし直してください。';
          }
        }
        setErrorText(errorMessage);
        setSnackbarOpen(true);
        setLoading(false);
      } finally {
        // 確実にローディングを止める
        setLoading(false);
      }
    },
    [
      patternId,
      resultLimit,
      user,
      selectedTab,
      watchSearchItems,
      isFieldEnabled,
      isDetailView,
      isProxyMode,
    ],
  );

  // fetchResultsの最新版をrefで保持（onCompletedコールバックのstale closure防止）
  fetchResultsRef.current = fetchResults;

  // スクロールで追加データ取得
  const fetchMoreData = (): void => {
    if (!loading && hasMore) {
      void fetchResults(lastVisible);
    }
  };

  // タイムスタンプ変換
  const formatTimestamp = (resultTimestamp: Timestamp): string => {
    return dayjs(resultTimestamp.seconds * 1000).format('YYYY/MM/DD HH:mm');
  };

  useEffect(() => {
    if (!patternId) {
      navigate('/pattern');
      return;
    }

    // 認証状態が確定するまで待機
    if (isLoading) {
      return;
    }

    // ユーザーが存在しない場合はリダイレクト処理を別のuseEffectに任せる
    if (!user) {
      return;
    }

    // 読取パターンIDが変更されたらフェッチ状態をリセット
    hasFetched.current = false;
    setRealtimeData([]);
    setSearchData([]);
    setLastVisible(null);
    setHasMore(true);
    setShowDataFlg(null);
    setIsCheckingPermission(true);
    setPattern(null);

    const fetchPattern = async (): Promise<void> => {
      try {
        const patternData = await getPattern(patternId);
        if (!patternData) {
          setErrorText('読取パターンが見つかりません');
          setSnackbarOpen(true);
          navigate('/pattern');
          return;
        }

        // 権限チェック: 運営スタッフ、または同じ会社のメンバー（作成者含む）のみアクセス可能
        const isOwner = patternData.created_by === user?.uid;
        const isSameCompany = user?.company_id && patternData.company_id === user.company_id;
        const isOperator = isOperatorStaff(user?.role);

        // 削除済みパターンは運営スタッフのみアクセス可能
        if (patternData.status === patternStatus.deleted && !isOperator) {
          setErrorText('この読取パターンは削除されています');
          setSnackbarOpen(true);
          navigate('/pattern');
          return;
        }

        // 運営スタッフは全てアクセス可能（company_idチェック不要）
        // 企業管理者・一般メンバー共通: 作成者または同じ会社であること（代理作成パターン含む）
        if (!isOperator && !isOwner && !isSameCompany) {
          setErrorText('この読取パターンにアクセスする権限がありません');
          setSnackbarOpen(true);
          navigate('/pattern');
          return;
        }

        // 権限チェック完了後にパターンデータをセット
        setIsCheckingPermission(false);
        setPattern(patternData);

        // 子パターン（#2967）。絞り込みの選択肢に使う。
        // 子パターンを持たないパターンでは取得も表示もしない
        if (patternData.has_sub_patterns) {
          getSubPatterns(patternId)
            .then(variants => setSubPatterns(variants.filter(v => v.is_active)))
            // 絞り込みの選択肢が出ないだけなので、失敗しても画面は止めない
            .catch(() => setSubPatterns([]));
        } else {
          setSubPatterns([]);
        }

        // 読取パターンのファイル処理方式をデフォルト値として設定
        if (patternData.file_type) {
          setProcessingTypeValue(patternData.file_type);
        }

        // 読取パターンのGeminiモデルをデフォルト値として設定
        if (patternData.gemini_model) {
          setSelectedGeminiModel(toAvailableGeminiModel(patternData.gemini_model));
          // パターンにProが設定されていれば一般ユーザーにもモデル選択を許可。
          // Pro の判定は共通化し、Pro モデルが増えたときの追従漏れを防ぐ（#3206）
          setIsProAllowedPattern(isProGeminiModel(patternData.gemini_model));
          // モデルに応じて思考設定の初期値をセット（Flash: 無効、Pro: 最小限）
          setThinkingBudgetValue(
            patternData.gemini_model === geminiModel.pro_2_5
              ? geminiThinkingBudget.minimal
              : geminiThinkingBudget.disabled,
          );
        }

        // 読取パターンのOCRエンジンをデフォルト値として設定（#3264）。
        // visionAi は 0 なので、値の有無は null/undefined で判定する。
        // 詳細モードONの運営が画面で選び直した場合はそちらが優先される
        const patternEngine =
          patternData.ocr_engine !== undefined && patternData.ocr_engine !== null
            ? patternData.ocr_engine
            : ocrEngineType.visionAi;
        setOcrEngine(patternEngine);
        setPatternOcrEngine(patternEngine);
        // DI系が設定されたパターンでは一般ユーザーにもエンジン選択を出す（#3264）。
        // 1つのパターンに手書きと表が混ざる場合に、帳票ごとに選び分けられるようにするため。
        // ユーザーへの課金は変わらない（原価のみ変動）ので、Pro のような単価上昇はない
        setIsOcrEngineSelectablePattern(patternEngine !== ocrEngineType.visionAi);
      } catch (error) {
        setErrorText('読取パターンの取得に失敗しました');
        setSnackbarOpen(true);
        navigate('/pattern');
      }
    };
    fetchPattern();
  }, [patternId, user, isLoading, navigate]);

  // パターンの共有範囲（#2804）。担当者候補の絞り込みに使用（#2927）
  // パターンオブジェクト全体ではなくスコープ値を依存にし、無関係な setPattern での再取得を防ぐ
  const isPatternLoaded = pattern !== null;
  const patternShareScope = pattern?.share_scope;
  const patternSharedGroupIds = pattern?.shared_group_ids;
  const patternSharedUserIds = pattern?.shared_user_ids;
  const patternOwnerUserId = pattern?.owner_user_id;

  // 担当者候補リストをロード（#2642）
  // 代理モード時は対象企業、通常時はログインユーザー所属会社
  // パターンの共有範囲（#2804）に応じ、結果を閲覧できるユーザーのみ候補にする（#2927）
  useEffect(() => {
    if (isLoading || !user) return;
    const companyId = effectiveCompanyId || user.company_id;
    if (!companyId) {
      setAssigneeOptions([]);
      setAssigneeDisplayOptions([]);
      return;
    }
    void (async (): Promise<void> => {
      try {
        const users = await getUsersByCompanyId(companyId);
        const finishedUsers = users.filter(u => u.register_status === registerStatus.finished);
        const byName = (a: AssigneeOption, b: AssigneeOption): number =>
          a.name.localeCompare(b.name, 'ja');

        // 表示解決用（#3095）: 共有範囲で絞らない全アクティブメンバー。
        // 共有範囲外になった現役メンバーを「退会済」と誤表示しないため、割り当て候補とは別に保持する。
        setAssigneeDisplayOptions(
          finishedUsers
            .map(u => ({
              uid: u.uid as string,
              name: (u.name as string) ?? '',
              iconUrl: (u.icon_url as string | undefined) ?? null,
            }))
            .sort(byName),
        );

        // 割り当て候補用（#2927）: パターン未ロード時は共有範囲が確定できないため候補を出さない。
        if (!isPatternLoaded) {
          setAssigneeOptions([]);
          return;
        }
        const resource = {
          share_scope: patternShareScope,
          shared_group_ids: patternSharedGroupIds,
          shared_user_ids: patternSharedUserIds,
          owner_user_id: patternOwnerUserId,
        };
        // 共有範囲外で結果を閲覧できないユーザーは担当者候補から除外する（#2927）
        setAssigneeOptions(
          finishedUsers
            .filter(u =>
              canViewSharedResource(
                {
                  uid: u.uid as string,
                  role: (u.role as number | null | undefined) ?? null,
                  group_ids: u.group_ids as string[] | undefined,
                },
                resource,
              ),
            )
            .map(u => ({
              uid: u.uid as string,
              name: (u.name as string) ?? '',
              iconUrl: (u.icon_url as string | undefined) ?? null,
            }))
            .sort(byName),
        );
      } catch {
        setAssigneeOptions([]);
        setAssigneeDisplayOptions([]);
      }
    })();
  }, [
    user,
    isLoading,
    effectiveCompanyId,
    isPatternLoaded,
    patternShareScope,
    patternSharedGroupIds,
    patternSharedUserIds,
    patternOwnerUserId,
  ]);

  // 残りアップロード可能枚数と利用枚数をリアルタイム監視（companiesドキュメントのcurrent_month_ocr_count/costを使用）
  useEffect(() => {
    if (!company?.company_id) return;

    // 代理モードの場合は監視をスキップ（代理OCRはカウント対象外）
    if (isProxyMode) {
      setBaseRemainingCount(9999);
      return;
    }

    const companyDocRef = doc(db, 'companies', company.company_id);
    const unsubscribe = onSnapshot(
      companyDocRef,
      snapshot => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const currentMonthOcrCount = data.current_month_ocr_count ?? 0;
          const currentMonthOcrCost = data.current_month_ocr_cost ?? 0;
          const ocrMonthlyBudget = data.ocr_monthly_budget ?? company.ocr_monthly_budget;
          const creditRate = getCreditRateByPlan(data.plan ?? company.plan);
          setUsedCount(currentMonthOcrCount);
          const baseRemaining = Math.floor((ocrMonthlyBudget - currentMonthOcrCost) / creditRate);
          setBaseRemainingCount(Math.max(0, baseRemaining));
          setBaseRemainingJpy(Math.max(0, ocrMonthlyBudget - currentMonthOcrCost));
        }
      },
      error => {
        // ログアウト直後にリスナーが permission-denied を受け取るのは正常な動作
        if (error instanceof FirebaseError && error.code === 'permission-denied') {
          return;
        }
        Sentry.captureException(error);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [company, isProxyMode]);

  // ファイルバリデーション・クレジット情報・上限超過判定は UploadModal 内に移管済み（develop仕様統合）

  useEffect(() => {
    if (!user || !patternId) {
      return;
    }

    const fetchData = async (): Promise<void> => {
      await fetchResults(null);
    };
    void fetchData();

    // クリーンアップ関数でリアルタイム取得を停止
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [user, patternId, selectedTab, fetchResults]);

  // 検索条件のいずれかが入力されたら Algolia で全件検索する
  useEffect(() => {
    if (!user || !patternId) {
      return;
    }

    if (searchKey) {
      freeWordFetchIdRef.current += 1;
      const currentFreeWordFetchId = freeWordFetchIdRef.current;

      // 条件変更時に古い検索結果を表示しない
      setSearchData([]);
      setAppliedFreeWordSearchKey('');

      const fetchAllData = async (): Promise<void> => {
        try {
          // カスタマイズで無効化された項目は検索条件から除外する (#2642 集約)
          const {
            responsiblePerson,
            assignee,
            startDate,
            endDate,
            ocrStatus: ocrStatusValue,
          } = extractEnabledSearchInputs(watchSearchItems, isFieldEnabled);

          let results: Record<string, unknown>[];
          try {
            // Algolia で全条件を一括検索
            results = (await searchPatternResultsByAlgolia({
              patternId,
              status: selectedTab,
              freeWord: algoliaSearchSpec.freeWord || null,
              fileName: algoliaSearchSpec.fileName || null,
              fileNameMatchMode: algoliaSearchSpec.fileNameMatchMode,
              ocrItemKeywords: algoliaSearchSpec.ocrItems,
              responsiblePerson,
              assignee,
              startDate,
              endDate,
              ocrStatus: ocrStatusValue,
              companyId: effectiveCompanyId || user?.company_id,
            })) as Record<string, unknown>[];

            if (currentFreeWordFetchId !== freeWordFetchIdRef.current) {
              return;
            }
          } catch (algoliaError) {
            // Algolia 障害時は Firestore のクエリ条件のみで取得（フリーワード/ファイル名/OCR 検索は無効）
            // 検索条件が一部適用されていないことをユーザーに通知し、意図しない結果と気付けるようにする
            // 原因調査のためエラー詳細をログ・Sentry に記録する
            // eslint-disable-next-line no-console
            console.error('[PatternAiOcr] Algolia検索エラー:', algoliaError);
            Sentry.captureException(algoliaError);
            setInfoText('フリーワード・ファイル名・読取項目の検索条件が一時的に適用されていません');
            setSnackbarOpen(true);
            results = await getPatternResultsForExport(
              patternId,
              selectedTab,
              {
                fileName: null,
                responsiblePerson,
                assignee,
                startDate,
                endDate,
                ocrStatus: ocrStatusValue,
                freeWord: null,
              },
              effectiveCompanyId || user?.company_id,
            );
          }

          const updatedResults: ResultWithUserType[] = results.map(data => {
            const resultData = data as unknown as ResultType;
            return {
              ...resultData,
              showName: getDisplayName(resultData.user_name, resultData.is_proxy),
              iconUrl: resultData.is_proxy ? HiStoryIcon : resultData.user_icon_url || null,
            } as ResultWithUserType;
          });

          // 要注意フィルタ（#3262）。Algolia には needs_review を載せていないため、
          // 取得後にクライアントサイドで絞る（設計書 §6.4）
          const filteredResults = filterBySubPattern(
            filterByNeedsReview(updatedResults, watchSearchItems.needsReview),
            watchSearchItems.subPatternId,
          );

          if (currentFreeWordFetchId !== freeWordFetchIdRef.current) {
            return;
          }

          setSearchData(filteredResults);
          setAppliedFreeWordSearchKey(searchKey);
        } catch (e) {
          if (currentFreeWordFetchId !== freeWordFetchIdRef.current) {
            return;
          }
          setErrorText('検索データの取得に失敗しました');
          setSnackbarOpen(true);
        }
      };
      void fetchAllData();
    } else {
      // 検索条件がクリアされた場合は検索データをクリア
      setSearchData([]);
      setAppliedFreeWordSearchKey('');
    }
    // 依存配列は searchKey に集約する（searchKey は全検索条件の JSON で、
    // 内容が変わったときだけ参照文字列が変わるため effect の再実行を最小化できる）。
    // useSnackbar の setter は useCallback 化されておらず毎レンダーで参照が変わるため
    // 依存配列には含めない（含めると無限ループになる）。
  }, [searchKey, user, patternId, effectiveCompanyId]);

  // 検索条件が変更された時の処理（フリーワード以外）
  useEffect(() => {
    if (!user || !patternId) {
      return;
    }

    // 読取パターンIDが変わった場合はスキップ（Pattern Effectで処理する）
    if (previousPatternId.current !== undefined && previousPatternId.current !== patternId) {
      return;
    }

    // 読取パターン切り替え中は、データ取得をスキップ（Pattern Effectで処理済み）
    if (isPatternChanging.current) {
      // フラグをリセット（次回の検索条件変更で動作するように）
      isPatternChanging.current = false;
      return;
    }

    // 検索条件変更時にデータをリセットして再取得
    setRealtimeData([]);
    setLastVisible(null);
    setHasMore(true);
    setShowDataFlg(null);
    setLoading(true); // 検索開始時にローディングを開始

    const fetchData = async (): Promise<void> => {
      await fetchResults(null);
    };
    void fetchData();
  }, [
    watchSearchItems.responsiblePerson,
    watchSearchItems.assignee,
    watchSearchItems.startDate,
    watchSearchItems.endDate,
    watchSearchItems.ocrStatus,
    user,
    selectedTab,
    fetchResults,
    patternId,
  ]);

  // 読取パターンID変更時に検索条件をクリアしてデータを再取得
  useEffect(() => {
    // 初回マウント時はスキップ
    if (previousPatternId.current === undefined) {
      previousPatternId.current = patternId;
      return;
    }

    // patternIdが変わった場合のみ検索条件をクリアしてデータをリセット
    if (previousPatternId.current !== patternId) {
      previousPatternId.current = patternId;

      // 読取パターン切り替え中フラグをON（検索条件変更のuseEffectをスキップさせる）
      isPatternChanging.current = true;

      // データをリセット
      setRealtimeData([]);
      setSearchData([]);
      setLastVisible(null);
      setHasMore(true);
      setShowDataFlg(null);
      hasFetched.current = false;
      setLoading(true);
      // チェックボックスの選択状態をクリア
      setSelectedIds(new Set());

      // 検索条件をクリア
      const clearedConditions = {
        fileName: null,
        responsiblePerson: null,
        assignee: null,
        startDate: null,
        endDate: null,
        freeWord: null,
      };
      setWatchSearchItems(clearedConditions);

      // 検索条件クリア後にデータを取得（クリアした条件を明示的に渡す）
      const fetchData = async (): Promise<void> => {
        await fetchResults(null, clearedConditions);
        // データ取得完了後にフラグをOFF
        isPatternChanging.current = false;
      };
      void fetchData();
    }
  }, [patternId, fetchResults]);

  // タブ別件数の取得（リアルタイム監視）
  // 注意（#2804 Phase3 既知の制約）: 件数はサーバー集計の総数で、共有範囲による
  // クライアント側アクセス制御フィルタは適用されない。一般メンバーでは「タブ件数 >
  // 実表示件数」になり得る。厳密一致にはサーバー側スコープ絞り込み（クエリ書換＋index）が必要。
  useEffect(() => {
    if (!patternId) {
      return;
    }

    const fetchCounts = async (): Promise<void> => {
      // 既存の購読を解除
      countUnsubscribeRef.current.forEach(unsub => unsub());
      countUnsubscribeRef.current = [];

      // 未処理件数の監視
      const unsubscribeProcessing = await getPatternResultsCountByStatus(
        patternId,
        processingStatus.processing,
        count => {
          setProcessingCount(count);
        },
      );
      countUnsubscribeRef.current.push(unsubscribeProcessing);

      // 処理済件数の監視
      const unsubscribeCompleted = await getPatternResultsCountByStatus(
        patternId,
        processingStatus.completed,
        count => {
          setCompletedCount(count);
        },
      );
      countUnsubscribeRef.current.push(unsubscribeCompleted);

      // 出力済件数の監視
      const unsubscribeOutputted = await getPatternResultsCountByStatus(
        patternId,
        processingStatus.outputted,
        count => {
          setOutputtedCount(count);
        },
      );
      countUnsubscribeRef.current.push(unsubscribeOutputted);
    };

    fetchCounts();

    // クリーンアップ関数
    return () => {
      countUnsubscribeRef.current.forEach(unsub => unsub());
      countUnsubscribeRef.current = [];
    };
  }, [patternId]);

  // タブ機能
  const TabItem = ({ status }: { status: number }): ReactElement => {
    const statusLabels = {
      [processingStatus.processing]: processingStatusLabel[processingStatus.processing],
      [processingStatus.completed]: processingStatusLabel[processingStatus.completed],
      [processingStatus.outputted]: processingStatusLabel[processingStatus.outputted],
    };

    // 件数の取得
    const getCount = (): number | null => {
      if (status === processingStatus.processing) {
        return processingCount;
      } else if (status === processingStatus.completed) {
        return completedCount;
      } else if (status === processingStatus.outputted) {
        return outputtedCount;
      }
      return null;
    };

    const count = getCount();
    const label = count !== null ? `${statusLabels[status]}（${count}件）` : statusLabels[status];

    const onClick = (): void => {
      handleTabChange(status);
    };

    return (
      <Box sx={[styles.tab, selectedTab === status && styles.tabActive]} onClick={onClick}>
        <Typography sx={[styles.tabText, selectedTab === status && styles.tabTextActive]}>
          {label}
        </Typography>
      </Box>
    );
  };

  const handleTabChange = (newValue: number): void => {
    if (newValue === selectedTab) return;
    setSelectedTab(newValue);
    // URLにも反映しておき、リロードや履歴画面からの復帰でタブを維持する
    // タブ切り替えで履歴を増やさないよう replace で更新する
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set(RESULT_TAB_PARAM_KEY, String(newValue));
        return next;
      },
      { replace: true },
    );
    // タブ変更時にデータをリセット
    hasFetched.current = false;
    setRealtimeData([]);
    setSearchData([]);
    setLastVisible(null);
    setHasMore(true);
    setShowDataFlg(null);
    // チェックボックスの選択状態をクリア
    setSelectedIds(new Set());
    // 要注意の絞り込みは未処理タブ以外では非表示になるため解除する（#3334）。
    // 残したままだと、見えない条件で常に0件になってしまう
    setWatchSearchItems(prev =>
      prev.needsReview === undefined ? prev : { ...prev, needsReview: undefined },
    );
  };

  const handleAddNew = async (): Promise<void> => {
    // 前回のアップロード成功時は onClose を通らずに閉じるため、submit済みフラグが残る。
    // 残ったままだと上限超過などのエラーが出ず、ボタンだけ押せなくなる（#3419）
    setIsSubmitted(false);

    // 代理モードの場合は制限チェックをスキップ（代理OCRはカウント・クレジット対象外）
    if (!isProxyMode) {
      // 月間枠超過かつOCRクレジットがない場合のみダイアログ表示
      // OCRクレジットがある場合はバックエンドのチェックに依存
      if (usedCount >= userMaxFiles && (!company || company.ocr_credit_balance <= 0)) {
        setLimitDialogOpen(true);
        return;
      }
    }

    // 担当者デフォルト値（#2642）。通常=本人 / 代理=企業担当者（取得不可なら未設定）
    const defaultAssignee = await resolveDefaultAssignee({
      isProxyMode,
      companyId: effectiveCompanyId,
      user: { uid: user?.uid ?? '' },
    });
    setSelectedAssigneeUid(defaultAssignee.uid ?? '');

    setUploadModalOpen(true);
  };

  // アップロードファイル選択時
  const handleFilesSelected = async (
    files: FileList | null,
    onChange: (value: File[]) => void,
  ): Promise<void> => {
    if (files) {
      const fileArray = Array.from(files);
      const currentImages = watch('images') || [];

      if (!user) {
        setErrorText('ログインしてください。');
        setSnackbarOpen(true);
        return;
      }

      const updatedFiles: File[] = [...currentImages, ...fileArray];

      // pagePerResultモードのPDFはページ数をキャッシュ（Chip展開表示用）
      if (processingTypeValue === fileProcessingType.pagePerResult) {
        const newCache = new Map(pdfPageCountCache);
        for (const file of fileArray) {
          if (isPdfFile(file) && !newCache.has(file.name)) {
            const count = await getPdfPageCount(file);
            newCache.set(file.name, count);
          }
        }
        setPdfPageCountCache(newCache);
      }

      // 常に選択した全ファイルを表示する
      onChange(updatedFiles);

      // ファイル追加後に即座にバリデーションを実行
      setTimeout(async () => {
        // バリデーションを手動で発火
        const isValid = await trigger('images');

        // 合計ページ数の上限チェック
        const totalPages = await calculateTotalPageCount(updatedFiles);
        if (isValid && totalPages > MAX_TOTAL_PAGES) {
          setError('images', {
            type: 'manual',
            message: `合計ページ数が上限を超えています。（${totalPages}ページ / 上限${MAX_TOTAL_PAGES}ページ）`,
          });
          return;
        }

        // singleResult時の1ファイルあたりPDFページ数上限チェック
        if (isValid && processingTypeValue === fileProcessingType.singleResult) {
          for (const file of fileArray) {
            if (isPdfFile(file)) {
              const pageCount = await getPdfPageCount(file);
              if (pageCount > MAX_PAGES_PER_FILE) {
                setError('images', {
                  type: 'manual',
                  message: `「${file.name}」のページ数が上限を超えています（${pageCount}ページ / 上限${MAX_PAGES_PER_FILE}ページ）。`,
                });
                return;
              }
            }
          }
        }

        // 上限枚数チェック（月間枠 + OCRクレジット分）- ページ数ベース
        const limitInfo = calculateOcrLimitInfo(company, baseRemainingCount);
        const overMessage = getOcrLimitOverMessage(totalPages, limitInfo);
        if (isValid && overMessage) {
          setError('images', { type: 'manual', message: overMessage });
        }
      }, 0);
    }
  };

  const handleFilesDeleted = (index: number): void => {
    const currentImages = watch('images');
    const updatedImages = currentImages.filter((_, i) => i !== index);
    reset({ images: updatedImages });
    trigger('images');
  };

  // 出力方式変更時のバリデーション再実行
  const handleProcessingTypeChange = async (
    newValue: number,
    currentFiles: File[],
  ): Promise<void> => {
    // UploadModal から呼ばれるため、処理方式 state の更新もここで行う（旧インラインダイアログ相当）
    setProcessingTypeValue(newValue);
    if (newValue === fileProcessingType.singleResult) {
      for (const file of currentFiles) {
        if (isPdfFile(file)) {
          const pageCount = await getPdfPageCount(file);
          if (pageCount > MAX_PAGES_PER_FILE) {
            setError('images', {
              type: 'manual',
              message: `「${file.name}」のページ数が上限を超えています（${pageCount}ページ / 上限${MAX_PAGES_PER_FILE}ページ）。`,
            });
            return;
          }
        }
      }
    }
    await trigger('images');
  };

  // データをリフレッシュ（アップロード後の新しいレコード表示用）
  // onCompletedコールバックとして渡されるため、ref経由で最新のfetchResultsを使用
  const refreshData = useCallback(async (): Promise<void> => {
    // データ状態をリセット
    setRealtimeData([]);
    setSearchData([]);
    setLastVisible(null);
    setHasMore(true);
    setShowDataFlg(null);

    // 新しいデータを取得（ref経由で常に最新のfetchResultsを呼ぶ）
    void fetchResultsRef.current(null);
  }, []);

  const handleDeleteResult = async (): Promise<void> => {
    try {
      await deleteResult(deleteTargetId);
      setDeleteModalOpen(false);

      // データをリフレッシュ
      await refreshData();

      setInfoText('ファイルを削除しました。');
      setSnackbarOpen(true);
    } catch (e) {
      setInfoText('');
      setErrorText('削除に失敗しました。');
      setSnackbarOpen(true);
    }
  };

  // チェックボックス関連の関数
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.checked) {
      const allIds = new Set(showData.map(data => data.id));
      setSelectedIds(allIds);
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: string): void => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // 担当者変更ダイアログを開く（単一・三点リーダーから）#2642
  const openSingleAssigneeDialog = (resultId: string): void => {
    const target = showData.find(d => d.id === resultId);
    setAssigneeDialogMode('single');
    setAssigneeDialogTargets([resultId]);
    setAssigneeDialogCurrent(target?.assignee_id ?? undefined);
    setAssigneeDialogLabel(target?.originalFiles?.[0]?.file_name);
    setAssigneeDialogOpen(true);
  };

  // 担当者変更ダイアログを開く（一括・ヘッダから）#2642
  const openBulkAssigneeDialog = (): void => {
    setAssigneeDialogMode('bulk');
    setAssigneeDialogTargets(Array.from(selectedIds));
    setAssigneeDialogCurrent(undefined);
    setAssigneeDialogLabel(undefined);
    setAssigneeDialogOpen(true);
  };

  // 担当者変更成功時のハンドラ #2642
  const handleAssigneeChangeSuccess = (): void => {
    if (assigneeDialogMode === 'bulk') {
      setSelectedIds(new Set());
    }
    setInfoText(assigneeLabels.changeSuccess);
    setSnackbarOpen(true);
    void refreshData();
  };

  const handleAssigneeChangeError = (message: string): void => {
    setErrorText(message);
    setSnackbarOpen(true);
  };

  const handleBulkDelete = async (): Promise<void> => {
    try {
      setBulkDeleteLoading(true);
      const deletePromises = Array.from(selectedIds).map(id => deleteResult(id));
      await Promise.all(deletePromises);

      setBulkDeleteModalOpen(false);
      setSelectedIds(new Set());

      // データをリフレッシュ
      await refreshData();

      setInfoText(`${deletePromises.length}件のファイルを削除しました。`);
      setSnackbarOpen(true);
    } catch (e) {
      setInfoText('');
      setErrorText('一括削除に失敗しました。');
      setSnackbarOpen(true);
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  /**
   * 要注意以外を一括で確認済にする（#3262）。
   *
   * 自動で確認済にする挙動は入れていない（設計書 §2.3）。
   * ユーザー自身が押した操作であることを result_history に残すため、
   * 更新は bulkUpdateProcessingStatus 経由で行う。
   */
  const handleBulkComplete = async (): Promise<void> => {
    if (bulkCompleteTargetIds.length === 0) return;

    setBulkCompleteProcessing(true);
    try {
      const { updatedCount, failedCount } = await bulkUpdateProcessingStatus({
        resultIds: bulkCompleteTargetIds,
        processingStatus: processingStatus.completed,
        updatedBy: user?.uid ?? '',
        updatedByName: user?.name ?? '',
      });

      await refreshData();

      // 部分失敗でも成功分はそのまま残す（設計書 §8.4）
      if (failedCount > 0) {
        setInfoText('');
        setErrorText(
          `${updatedCount}件を確認済にしました。${failedCount}件は変更できませんでした。`,
        );
      } else {
        setErrorText('');
        setInfoText(`${updatedCount}件を確認済にしました。`);
      }
      setSnackbarOpen(true);
    } catch (e) {
      setInfoText('');
      setErrorText('一括での確認済への変更に失敗しました。');
      setSnackbarOpen(true);
    } finally {
      setBulkCompleteProcessing(false);
      setBulkCompleteDialogOpen(false);
    }
  };

  // 仕分け変更の残高チェック（一括・個別共通）
  const checkCreditBeforeReclassify = (newPatternId: string, isBulk: boolean): boolean => {
    // 代理モード・「パターンなし」選択時はチェック不要
    if (isProxyMode) return true;
    if (newPatternId === PATTERN_ID_OTHER) return true;
    if (!company) return true;

    const count = isBulk ? selectedIds.size : 1;
    const limitInfo = calculateOcrLimitInfo(company, baseRemainingCount);

    // 残高なし
    if (limitInfo.totalRemainingCount < count) {
      setReclassifyModalOpen(false);
      setLimitDialogOpen(true);
      return false;
    }

    return true;
  };

  /** 子パターン再読み取りの残高チェック。仕分け変更と同じ基準で見る（#2967） */
  const handleBulkSubPatternReOcr = (subPatternId: string): void => {
    if (!isProxyMode && company) {
      const limitInfo = calculateOcrLimitInfo(company, baseRemainingCount);
      if (limitInfo.totalRemainingCount < selectedIds.size) {
        setSubPatternReOcrModalOpen(false);
        setLimitDialogOpen(true);
        return;
      }
    }
    void executeBulkSubPatternReOcr(subPatternId);
  };

  /**
   * 子パターンを指定して一括で再読み取りする（#2967）。
   *
   * 仕分け変更と違い**パターンは変えない**。同じパターンのまま、
   * 適用する子パターンだけを差し替えて読み直す。
   * AIの判定が外れた帳票をまとめて直すための導線。
   */
  const executeBulkSubPatternReOcr = async (subPatternId: string): Promise<void> => {
    setSubPatternReOcrModalOpen(false);
    setReclassifyLoading(true);

    if (!patternId) return;

    const targetIds = Array.from(selectedIds);
    let provisionalDeducted = false;

    // 各レコードのページ数を取得（クレジットの仮消費と割り当てに使う）
    const targetPageCounts = new Map<string, number>();
    for (const id of targetIds) {
      const result = showData.find(d => d.id === id);
      targetPageCounts.set(id, result?.originalFiles?.length || result?.files?.length || 1);
    }
    const totalPages = Array.from(targetPageCounts.values()).reduce((sum, p) => sum + p, 0);

    // パターンは変わらないため、モデルとマスタ照合の有無は現在のパターンのものを使う
    const currentGeminiModel = pattern?.gemini_model ?? GEMINI_MODEL_DEFAULT;
    const hasMasterMapping = pattern?.has_master_mapping === true;

    try {
      if (company) {
        await executeProvisionalOcrUsage(
          company,
          totalPages,
          baseRemainingCount,
          isProxyMode,
          currentGeminiModel,
          hasMasterMapping,
          1,
          [],
          baseRemainingJpy,
        );
        provisionalDeducted = true;
      }

      // 読み取り中の表示にする（完了は onSnapshot で反映される）
      for (const resultId of targetIds) {
        await editResult({
          resultId,
          data: { ocr_status: ocrStatus.processing },
          userId: user?.uid,
        });
      }

      setSelectedIds(new Set());
      setInfoText(`${targetIds.length}件を再読み取りしています`);
      setSnackbarOpen(true);
      setReclassifyLoading(false);

      // バックグラウンドで再読取を実行（完了を待たない）。
      // 月間枠を超えるページ分だけクレジットを割り当てる（一括仕分け変更と同じ計算）
      const creditRate = company ? getOcrCreditRateWithModel(company.plan, currentGeminiModel) : 0;
      const budgetRemainingCount =
        baseRemainingCount !== null ? Math.max(0, baseRemainingCount) : 0;
      let cumulativePages = 0;
      for (const resultId of targetIds) {
        const resultPageCount = targetPageCounts.get(resultId) || 1;
        const pagesBeforeThis = cumulativePages;
        cumulativePages += resultPageCount;
        const excessPages =
          Math.max(0, cumulativePages - budgetRemainingCount) -
          Math.max(0, pagesBeforeThis - budgetRemainingCount);
        void handleReOcrPatternResult({
          resultId,
          patternId,
          itemList: (pattern?.item_list ?? []) as unknown as Record<string, unknown>[],
          subPatternId,
          provisionalDeducted,
          feProvisionalCreditAmount: excessPages * creditRate,
          feProvisionalUsageCount: resultPageCount,
          isProxy: isProxyMode,
          operatorId: isProxyMode ? operatorId || undefined : undefined,
        });
      }
    } catch {
      setErrorText('再読み取りに失敗しました');
      setReclassifyLoading(false);
    }
  };

  // 一括仕分け変更
  const handleBulkReclassify = (
    newPatternId: string,
    itemList: Record<string, unknown>[],
  ): void => {
    if (!checkCreditBeforeReclassify(newPatternId, true)) return;
    void executeBulkReclassify(newPatternId, itemList);
  };

  const executeBulkReclassify = async (
    newPatternId: string,
    itemList: Record<string, unknown>[],
  ): Promise<void> => {
    setReclassifyModalOpen(false);
    setReclassifyLoading(true);

    const targetIds = Array.from(selectedIds);
    const totalCount = targetIds.length;
    let provisionalDeducted = false;

    // 各レコードのページ数を取得
    const targetPageCounts = new Map<string, number>();
    for (const id of targetIds) {
      const result = showData.find(d => d.id === id);
      targetPageCounts.set(id, result?.originalFiles?.length || result?.files?.length || 1);
    }
    const totalPages = Array.from(targetPageCounts.values()).reduce((sum, p) => sum + p, 0);

    // 仕分け先パターンのgemini_modelを取得（Pro単価の仮引き・OCR実行に使用）
    const reclassifyPattern =
      newPatternId !== PATTERN_ID_OTHER ? await getPattern(newPatternId).catch(() => null) : null;
    const reclassifyGeminiModel = reclassifyPattern?.gemini_model ?? GEMINI_MODEL_DEFAULT;
    const reclassifyHasMasterMapping = reclassifyPattern?.has_master_mapping === true;

    try {
      // 「パターンなし」選択時はOCR不要なので仮消費しない
      if (newPatternId !== PATTERN_ID_OTHER && company) {
        // 仮消費実行（合計ページ数分 + マスタ照合が有効なら +15円）
        await executeProvisionalOcrUsage(
          company,
          totalPages,
          baseRemainingCount,
          isProxyMode,
          reclassifyGeminiModel,
          reclassifyHasMasterMapping,
          1,
          [],
          baseRemainingJpy,
        );
        provisionalDeducted = true;
      }

      // フロントからpattern_idを即座に変更（onSnapshotで自動的にリストから消える）
      const statusData =
        newPatternId === PATTERN_ID_OTHER
          ? { pattern_id: newPatternId, ocr_status: ocrStatus.error }
          : { pattern_id: newPatternId, ocr_status: ocrStatus.processing };
      for (const resultId of targetIds) {
        await editResult({ resultId, data: statusData, userId: user?.uid });
      }

      setSelectedIds(new Set());
      setInfoText(`${totalCount}件の仕分けを変更しました`);
      setSnackbarOpen(true);
      setReclassifyLoading(false);

      // バックグラウンドでOCR再読取を実行（完了を待たない）
      // 通常OCRと同様に、月間枠内タスクにはcreditAmount=0、超過分タスクにはcreditRate*pageCountを割り当て
      const creditRate = company
        ? getOcrCreditRateWithModel(company.plan, reclassifyGeminiModel)
        : 0;
      const budgetRemainingCount =
        baseRemainingCount !== null ? Math.max(0, baseRemainingCount) : 0;
      let cumulativePages = 0;
      for (const resultId of targetIds) {
        const resultPageCount = targetPageCounts.get(resultId) || 1;
        // このタスクのページのうち、月間枠を超過する分を計算
        const pagesBeforeThis = cumulativePages;
        cumulativePages += resultPageCount;
        const excessPages =
          Math.max(0, cumulativePages - budgetRemainingCount) -
          Math.max(0, pagesBeforeThis - budgetRemainingCount);
        const taskCreditAmount = excessPages * creditRate;
        void handleReOcrPatternResult({
          resultId,
          patternId: newPatternId,
          itemList,
          isReclassification: true,
          provisionalDeducted,
          feProvisionalCreditAmount: taskCreditAmount,
          feProvisionalUsageCount: resultPageCount,
          isProxy: isProxyMode,
          operatorId: isProxyMode ? operatorId || undefined : undefined,
        });
      }
    } catch (error) {
      const message = getUserErrorMessage(error, '仕分け変更に失敗しました');
      setErrorText(message);
      setSnackbarOpen(true);
      setReclassifyLoading(false);
    }
  };

  // 個別レコードの仕分け変更
  const handleSingleReclassify = (
    newPatternId: string,
    itemList: Record<string, unknown>[],
  ): void => {
    if (!singleReclassifyTargetId) return;
    if (!checkCreditBeforeReclassify(newPatternId, false)) return;
    void executeSingleReclassify(newPatternId, itemList);
  };

  const executeSingleReclassify = async (
    newPatternId: string,
    itemList: Record<string, unknown>[],
  ): Promise<void> => {
    if (!singleReclassifyTargetId) return;
    setReclassifyModalOpen(false);
    setReclassifyLoading(true);

    // 該当レコードのページ数を取得
    const targetResult = showData.find(d => d.id === singleReclassifyTargetId);
    const singlePageCount = targetResult?.originalFiles?.length || targetResult?.files?.length || 1;

    let feProvisionalCreditAmount = 0;
    let provisionalDeducted = false;

    try {
      // 「パターンなし」選択時はOCR不要なので仮消費しない
      if (newPatternId !== PATTERN_ID_OTHER && company) {
        // 仕分け先パターンのgemini_modelを取得（Pro単価の仮引きに使用）
        const singleReclassifyPattern = await getPattern(newPatternId);
        const singleReclassifyGeminiModel =
          singleReclassifyPattern?.gemini_model ?? GEMINI_MODEL_DEFAULT;
        const singleReclassifyHasMasterMapping =
          singleReclassifyPattern?.has_master_mapping === true;
        const provisionalResult = await executeProvisionalOcrUsage(
          company,
          singlePageCount,
          baseRemainingCount,
          isProxyMode,
          singleReclassifyGeminiModel,
          singleReclassifyHasMasterMapping,
          1,
          [],
          baseRemainingJpy,
        );
        feProvisionalCreditAmount = provisionalResult.feProvisionalCreditAmount;
        provisionalDeducted = true;
      }

      // フロントからpattern_idを即座に変更（onSnapshotで自動的にリストから消える）
      const statusData =
        newPatternId === PATTERN_ID_OTHER
          ? { pattern_id: newPatternId, ocr_status: ocrStatus.error }
          : { pattern_id: newPatternId, ocr_status: ocrStatus.processing };
      await editResult({
        resultId: singleReclassifyTargetId,
        data: statusData,
        userId: user?.uid,
      });

      setInfoText('仕分けを変更しました');
      setSnackbarOpen(true);
      setReclassifyLoading(false);
      setSingleReclassifyTargetId('');

      // バックグラウンドでOCR再読取を実行（完了を待たない）
      void handleReOcrPatternResult({
        resultId: singleReclassifyTargetId,
        patternId: newPatternId,
        itemList,
        isReclassification: true,
        provisionalDeducted,
        feProvisionalCreditAmount,
        feProvisionalUsageCount: singlePageCount,
        isProxy: isProxyMode,
        operatorId: isProxyMode ? operatorId || undefined : undefined,
      });
    } catch (error) {
      const message = getUserErrorMessage(error, '仕分け変更に失敗しました');
      setErrorText(message);
      setSnackbarOpen(true);
      setReclassifyLoading(false);
      setSingleReclassifyTargetId('');
    }
  };

  const isAllSelected = showData.length > 0 && selectedIds.size === showData.length;
  const isIndeterminate = selectedIds.size > 0 && selectedIds.size < showData.length;

  // ファイル出力ボタンのラベル（選択中は件数を表示。#2885）
  // ※押下は出力モーダルを開くだけで、実際の出力進捗は遷移先の出力履歴画面で表示するため
  //   ここでは「出力中」表記は出さない（loading はデータ読込中＝disabled/スピナーで表現）
  const fileExportButtonLabel =
    selectedIds.size > 0 ? `ファイル出力 (${selectedIds.size})` : 'ファイル出力';

  // ファイルアップロード処理
  const onSubmit = async (data: { images: File[] }): Promise<void> => {
    // submit開始時にフラグを設定してバリデーションメッセージを非表示にする
    setIsSubmitted(true);

    if (!patternId || !pattern || !user) {
      setErrorText('読取パターン情報またはユーザー情報が見つかりません');
      setSnackbarOpen(true);
      return;
    }

    // ファイル数上限チェック（代理モード時はスキップ：カウント・クレジット対象外）
    if (!isProxyMode) {
      if (!company) {
        setErrorText('企業情報の取得に失敗しました。');
        setSnackbarOpen(true);
        return;
      }
      const uploadPageCount = await calculateTotalPageCount(data.images);
      // 上限枚数チェック（月間枠 + OCRクレジット分）- ページ数ベース
      if (baseRemainingCount !== null && company) {
        const limitInfo = calculateOcrLimitInfo(company, baseRemainingCount);

        const overMessage = getOcrLimitOverMessage(uploadPageCount, limitInfo);
        if (overMessage) {
          setErrorText(overMessage);
          setSnackbarOpen(true);
          return;
        }
      }

      // 個人別 月間OCR上限チェック（#2804 Phase4）。代理モードは対象外
      // 選択時にもインライン表示するようになった（#3133）が、
      // 表示後に他端末で消費された場合に備え最終防波堤として最新値で再判定する。
      const execUserDoc = await getUser(user.uid);
      const monthlyLimit = getUserMonthlyOcrLimit((execUserDoc as UserType | undefined) ?? null);
      if (monthlyLimit !== null && (effectiveCompanyId ?? user.company_id)) {
        const companyIdForUsage = (effectiveCompanyId ?? user.company_id) as string;
        const usedPages = await getMonthlyUsageCountByUserId(companyIdForUsage, user.uid);
        const userLimitMessage = getUserMonthlyOcrLimitMessage(
          monthlyLimit,
          usedPages,
          uploadPageCount,
        );
        if (userLimitMessage) {
          setErrorText(userLimitMessage);
          setSnackbarOpen(true);
          return;
        }
      }
    }

    // 合計ページ数の上限チェック（サーバー負荷軽減のため）
    const uploadPageCountForLimit = await calculateTotalPageCount(data.images);
    if (uploadPageCountForLimit > MAX_TOTAL_PAGES) {
      setErrorText(
        `合計ページ数が上限を超えています。（${uploadPageCountForLimit}ページ / 上限${MAX_TOTAL_PAGES}ページ）`,
      );
      setSnackbarOpen(true);
      return;
    }

    // singleResult（1ファイル=1結果）の場合、1ファイルあたりのページ数上限をチェック
    if (processingTypeValue === fileProcessingType.singleResult) {
      for (const file of data.images) {
        if (isPdfFile(file)) {
          const pageCount = await getPdfPageCount(file);
          if (pageCount > MAX_PAGES_PER_FILE) {
            setErrorText(
              `「${file.name}」のページ数が上限を超えています（${pageCount}ページ / 上限${MAX_PAGES_PER_FILE}ページ）。`,
            );
            setSnackbarOpen(true);
            return;
          }
        }
      }
    }

    setErrorText('');

    // 運営スタッフ、またはProが許可されたパターンの場合はユーザー選択モデルを使用
    const modelToUse =
      isOperatorStaff(user?.role) || isProAllowedPattern
        ? selectedGeminiModel
        : GEMINI_MODEL_DEFAULT;

    // Company情報を取得してAI学習許可フラグを取得
    let allowAiLearning = true;
    if (user.company_id) {
      try {
        const companyData = await getCompanyForAccount(user.company_id);
        if (companyData) {
          allowAiLearning = companyData.allow_ai_learning;
        }
      } catch {
        // エラー時もデフォルト値(true)を使用して処理継続
      }
    }

    // ページ展開済みファイル名リストを生成（パネル表示用）
    const pageFileNames: string[] = [];
    for (const file of data.images) {
      if (isPdfFile(file) && processingTypeValue === fileProcessingType.pagePerResult) {
        const count = await getPdfPageCount(file);
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        const ext = file.name.match(/\.[^/.]+$/)?.[0] ?? '';
        for (let i = 1; i <= count; i++) {
          pageFileNames.push(count > 1 ? `${nameWithoutExt}_${i}${ext}` : file.name);
        }
      } else {
        pageFileNames.push(file.name);
      }
    }

    // 担当者情報を解決（#2642）
    // 未選択（空文字）の場合は null として保存（#2642 レビューFB）
    // 候補リストに見つからなかった場合も作成者の値にフォールバックせず null を保存する（#2872）
    const selectedAssignee = selectedAssigneeUid
      ? assigneeOptions.find(o => o.uid === selectedAssigneeUid)
      : null;
    const resolvedAssigneeId: string | null = selectedAssignee?.uid ?? null;
    const resolvedAssigneeName: string | null = selectedAssignee?.name ?? null;
    const resolvedAssigneeIconUrl: string | null = selectedAssignee?.iconUrl ?? null;

    // 実際に生成される結果数（#3131）
    // pagePerResult: 1ページ=1結果なので合計ページ数 / singleResult: 1ファイル=1結果なのでファイル数
    const resultCount =
      processingTypeValue === fileProcessingType.pagePerResult
        ? uploadPageCountForLimit
        : data.images.length;

    // バックグラウンドでアップロード開始
    startUpload({
      type: 'pattern',
      files: data.images,
      originalFiles: data.images,
      totalPageCount: resultCount,
      pageFileNames,
      userId: user.uid,
      userName: user.name || '',
      userIconUrl: user.icon,
      assigneeId: resolvedAssigneeId,
      assigneeName: resolvedAssigneeName,
      assigneeIconUrl: resolvedAssigneeIconUrl,
      processingType: processingTypeValue,
      geminiModel: modelToUse,
      companyId: effectiveCompanyId ?? user.company_id ?? undefined,
      allowAiLearning,
      isProxyMode,
      operatorId: isProxyMode ? operatorId || undefined : undefined,
      operatorRole: isProxyMode ? operatorRole || undefined : undefined,
      company,
      baseRemainingCount,
      baseRemainingJpy,
      patternId,
      patternName: pattern.pattern_name || patternId,
      itemList: pattern.item_list,
      hasMasterMapping: pattern.has_master_mapping === true,
      thinkingBudget: isOperatorStaff(user?.role)
        ? (thinkingBudgetValue ??
          (modelToUse === geminiModel.pro_2_5
            ? geminiThinkingBudget.minimal
            : geminiThinkingBudget.disabled))
        : modelToUse === geminiModel.pro_2_5
          ? geminiThinkingBudget.minimal
          : geminiThinkingBudget.disabled,
      thinkingLevel: isOperatorStaff(user?.role) ? thinkingLevelValue : null,
      // OCRエンジン（#3264）。
      // 運営（詳細モードON）か、DI系が設定されたパターンなら画面で選んだ値を使う。
      // それ以外はパターンの設定値（未設定なら visionAi）で従来どおりの動作になる
      ocrEngine:
        canUseVerificationSettings || isOcrEngineSelectablePattern ? ocrEngine : patternOcrEngine,
      // Geminiサンプリングパラメータは検証設定利用可なら常に送信（デフォルト temp=0 / topP=0）。
      // 一般ユーザー（利用不可）は undefined でモデルデフォルトに委ねる。
      geminiTemperature: canUseVerificationSettings ? geminiTemperature : undefined,
      geminiTopP: canUseVerificationSettings ? geminiTopP : undefined,
      includeCoordinates: canUseVerificationSettings ? includeCoordinates : false,
      structuredEngine: canUseVerificationSettings ? structuredEngine : structuredEngineType.gemini,
      onCompleted: refreshData,
    });

    // モーダルを閉じてフォームリセット
    setUploadModalOpen(false);
    reset({ images: [] });

    setInfoText(`${resultCount}件のAI読取タスクが開始されました。完了までしばらくお待ちください。`);
    setSnackbarOpen(true);
  };

  // データが0件の時のtitleの見出しテキスト
  const titleLabel = ((): string => {
    const patternName = pattern?.pattern_name || '';
    switch (selectedTab) {
      case processingStatus.completed:
        return patternName
          ? `${patternName}の${processingStatusLabel[processingStatus.completed]}ファイルがありません`
          : `${processingStatusLabel[processingStatus.completed]}ファイルがありません`;
      case processingStatus.outputted:
        return patternName
          ? `${patternName}の${processingStatusLabel[processingStatus.outputted]}ファイルがありません`
          : `${processingStatusLabel[processingStatus.outputted]}ファイルがありません`;
      case processingStatus.processing:
      default:
        return patternName
          ? `${patternName}の${processingStatusLabel[processingStatus.processing]}ファイルがありません`
          : `${processingStatusLabel[processingStatus.processing]}ファイルがありません`;
    }
  })();

  // データが0件の時のdescriptionの見出しテキスト
  const descriptionLabel = ((): string => {
    switch (selectedTab) {
      case processingStatus.completed:
        return `${processingStatusLabel[processingStatus.processing]}ファイルを処理すると${processingStatusLabel[processingStatus.completed]}タブに移行されます。\nAI読取結果画面から「${processingStatusLabel[processingStatus.completed]}にする」を押下してください。`;
      case processingStatus.outputted:
        return `${processingStatusLabel[processingStatus.processing]}・${processingStatusLabel[processingStatus.completed]}ファイルを出力すると${processingStatusLabel[processingStatus.outputted]}タブに移行されます。\n右上の「ファイル出力」を押下して実行してください。`;
      case processingStatus.processing:
      default:
        return `AI読取が完了すると${processingStatusLabel[processingStatus.processing]}タブに表示されます。\n「新規文字読取」ボタンからAI読取を実行してください。`;
    }
  })();

  // ファイル出力処理（CSV/エクセル対応）
  const handleFileExport = async (
    fileType: (typeof exportFileType)[keyof typeof exportFileType],
    encoding: (typeof exportEncoding)[keyof typeof exportEncoding],
    selectedFields: string[],
    sortedPattern?: PatternType,
    moveToOutputted?: boolean,
    saveSetting?: boolean,
    selectedFixedFields?: string[],
    sortedUnifiedOrder?: string[],
    customFileName?: string,
    customHeaders?: Record<string, string>,
    quoteMode?: (typeof exportQuoteMode)[keyof typeof exportQuoteMode],
  ): Promise<void> => {
    if (!patternId || !pattern || !user) {
      setErrorText('読取パターン情報が見つかりません');
      setSnackbarOpen(true);
      return;
    }

    // 設定保存処理
    if (saveSetting && pattern) {
      try {
        const itemListToUse = sortedPattern?.item_list || pattern.item_list;

        const sortedChildOrder: Record<string, string[]> = {};
        itemListToUse.forEach(item => {
          if (item.children?.length > 0) {
            sortedChildOrder[item.id] = item.children.map(child => child.id);
          }
        });

        // sortedUnifiedOrderがあれば統合リスト形式で保存、なければ読取項目のみ
        const sortedItemOrder = sortedUnifiedOrder || itemListToUse.map(item => item.id);

        const exportSettings: ExportSettingsType = {
          move_to_outputted: moveToOutputted ?? true,
          file_type: fileType,
          encoding,
          selected_fields: selectedFields,
          selected_fixed_fields: selectedFixedFields,
          sorted_item_order: sortedItemOrder,
          sorted_child_order: sortedChildOrder,
          custom_headers: customHeaders ?? {},
          quote_mode: quoteMode ?? exportQuoteMode.minimal,
        };

        const patternRef = doc(db, 'patterns', pattern.patternId);
        await updateDoc(patternRef, {
          export_settings: exportSettings,
          updated_at: Timestamp.now(),
        });
        setPattern(prev => (prev ? { ...prev, export_settings: exportSettings } : prev));
      } catch {
        // 設定保存失敗はエクスポート処理を止めない
      }
    }

    try {
      // ソート済み読取パターンがあればそれを使用、なければオリジナルを使用
      const patternToUse = sortedPattern || pattern;

      // Firestoreに履歴を作成（出力中ステータス）。遷移先のonSnapshotで反映される。
      // ユーザー指定のファイル名があればそれを優先、なければ自動生成
      const ext = fileType === exportFileType.excel ? '.xlsx' : '.csv';
      const trimmedCustomName = customFileName?.trim();
      const tempFileName = trimmedCustomName
        ? `${trimmedCustomName}${ext}`
        : generateFileName(patternToUse, selectedTab, fileType, encoding);

      const historyId = await createFileExportHistory({
        pattern_id: patternId,
        pattern_name: pattern.pattern_name,
        company_id: effectiveCompanyId ?? user.company_id ?? '',
        file_name: tempFileName,
        file_type: fileType === exportFileType.excel ? 'xlsx' : 'csv',
        created_by: user.uid,
        created_by_name: isProxyMode ? PROXY_DISPLAY_NAME : user.name,
        created_by_icon_url: isProxyMode ? '' : user.icon || '',
        is_proxy: isProxyMode || false,
      });

      // 履歴作成成功後に画面遷移（出力中ステータスが即座に表示される）
      navigate(`/ai-ocr/pattern/${patternId}/exports${buildResultTabQuery(selectedTab)}`);

      // 重い処理（データ取得・整形・ファイル生成・アップロード）はバックグラウンドで継続
      (async (): Promise<void> => {
        try {
          // 一覧表示と同じ検索条件・経路で取得して、絞り込み結果と乖離させない (#2642 集約)
          const {
            responsiblePerson,
            assignee,
            startDate,
            endDate,
            ocrStatus: ocrStatusValue,
          } = extractEnabledSearchInputs(watchSearchItems, isFieldEnabled);

          let results: DocumentData[];
          if (hasActiveSearch) {
            try {
              results = await searchPatternResultsByAlgolia({
                patternId,
                status: selectedTab,
                freeWord: algoliaSearchSpec.freeWord || null,
                fileName: algoliaSearchSpec.fileName || null,
                fileNameMatchMode: algoliaSearchSpec.fileNameMatchMode,
                ocrItemKeywords: algoliaSearchSpec.ocrItems,
                responsiblePerson,
                assignee,
                startDate,
                endDate,
                ocrStatus: ocrStatusValue,
                companyId: effectiveCompanyId || user?.company_id,
              });
            } catch (algoliaError) {
              // Algolia 障害時は Firestore で取得可能な条件のみで出力
              // 原因調査のためエラー詳細をログ・Sentry に記録する
              // eslint-disable-next-line no-console
              console.error('[PatternAiOcr] Algolia検索エラー (export):', algoliaError);
              Sentry.captureException(algoliaError);
              setInfoText(
                'フリーワード・ファイル名・読取項目の検索条件が一時的に適用されない状態でエクスポートしました',
              );
              setSnackbarOpen(true);
              results = await getPatternResultsForExport(
                patternId,
                selectedTab,
                {
                  fileName: null,
                  responsiblePerson,
                  assignee,
                  startDate,
                  endDate,
                  ocrStatus: ocrStatusValue,
                  freeWord: null,
                },
                effectiveCompanyId || user?.company_id,
              );
            }
          } else {
            results = await getPatternResultsForExport(
              patternId,
              selectedTab,
              {
                fileName: null,
                responsiblePerson,
                assignee,
                startDate,
                endDate,
                ocrStatus: ocrStatusValue,
                freeWord: null,
              },
              effectiveCompanyId || user?.company_id,
            );
          }

          // チェック行のみ出力（#2803）
          //   - 1件以上チェック時: 取得結果を選択行（selectedIds）に絞る
          //   - 未チェック時: 従来通りフィルタ結果全件を出力
          // 後続の「出力済」遷移（results.map）も同じ配列を参照するため、出力した行のみが outputted に遷移する
          if (selectedIds.size > 0) {
            results = results.filter(r => selectedIds.has(r.id));
          }

          if (results.length === 0) {
            // データがない場合はCloud Function側でエラー処理される想定
            // または、ここでFirestoreのステータスをエラーに更新
            return;
          }

          // データ整形（重い処理）
          const exportData = await generatePatternExportData(
            results,
            patternToUse,
            selectedFields,
            selectedFixedFields,
          );
          const columns = generateExportColumns(
            patternToUse,
            selectedFields,
            selectedFixedFields,
            sortedUnifiedOrder,
            customHeaders,
          );

          // ファイル生成（Blob作成）
          const fileBlob = await buildExportBlob(exportData, columns, fileType, encoding, {
            quoteMode,
          });

          // BlobをBase64に変換
          const fileContent = await new Promise<string>((resolve, reject): void => {
            const reader = new FileReader();
            reader.onloadend = (): void => {
              const base64String = (reader.result as string).split(',')[1];
              resolve(base64String);
            };
            reader.onerror = reject;
            reader.readAsDataURL(fileBlob);
          });

          // Cloud Functionsでファイルアップロード
          const exportResultIds = results.map(r => r.id);
          await exportFile({
            history_id: historyId,
            file_content: fileContent,
            file_size: fileBlob.size,
            encoding: encoding === exportEncoding.shiftJis ? 'sjis' : 'utf8',
            result_ids: exportResultIds,
            result_count: exportResultIds.length,
          });

          // 出力済ステータスへの移行
          if (moveToOutputted) {
            try {
              const resultIds = results.map(r => r.id);
              const updatePromises = resultIds.map(id => {
                const resultRef = doc(db, 'results', id);
                return updateDoc(resultRef, {
                  processing_status: processingStatus.outputted,
                  updated_at: Timestamp.now(),
                  updated_by: user.uid,
                });
              });
              await Promise.all(updatePromises);
            } catch {
              // ステータス更新失敗はファイル出力自体を失敗にしない
            }
          }
        } catch (error) {
          Sentry.captureException(error);
          // エラーはCloud Function側でFirestoreのステータスを更新する
        }
      })();
    } catch (error) {
      Sentry.captureException(error);
      // 履歴作成までで失敗したケース。遷移していないのでソース画面でスナックバー通知。
      setErrorText('ファイル出力に失敗しました。');
      setSnackbarOpen(true);
      // 失敗をダイアログに伝播し、モーダルを開いたままリトライ可能にする
      throw error;
    }
  };

  // 権限チェック中またはパターンデータ取得中はローディング表示（サイドバーは維持）
  if (isCheckingPermission || !pattern) {
    return (
      <Box sx={layoutStyles.pageWrapper}>
        <MetaTitle customTitle="AI読取" />
        <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
          <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
        </Box>
        <Box sx={[layoutStyles.pageContent, sideBarCollapsed && layoutStyles.pageContentCollapsed]}>
          <CustomLoading loading={true} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle customTitle={`${pattern.pattern_name} - AI読取`} />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>

      <Box sx={[layoutStyles.pageContent, sideBarCollapsed && layoutStyles.pageContentCollapsed]}>
        {reclassifyLoading && <CustomLoading loading={true} />}
        <Box sx={layoutStyles.header}>
          <Box sx={styles.titleAndStatusWrapper}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={styles.headerTitle}>{`${pattern.pattern_name} - AI読取`}</Typography>
              {isProGeminiModel(pattern.gemini_model) && (
                <Tooltip title={PATTERN_MODEL_SELECTABLE_TOOLTIP} arrow>
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      fontSize: '10px',
                      padding: '2px 6px',
                      backgroundColor: navy50,
                      color: white,
                      borderRadius: '4px',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      fontWeight: 'bold',
                    }}
                  >
                    Pro
                  </Box>
                </Tooltip>
              )}
              {pattern.status === patternStatus.deleted && <DeletedBadge />}
            </Box>
          </Box>
          <Box sx={styles.headerButtonWrapper}>
            {pattern.status !== patternStatus.deleted && (
              <Button
                onClick={(): void => {
                  void handleAddNew();
                }}
                startIcon={<CloudUploadOutlined sx={styles.icon24} />}
                sx={[primaryButtonBackgroundColor, styles.buttonLarge]}
              >
                <Typography sx={buttonTextIconLarge}>新規文字読取</Typography>
              </Button>
            )}
            <Button
              startIcon={<TuneIcon sx={styles.icon24} />}
              sx={[tertiaryButtonBackgroundColor, styles.buttonLarge]}
              disabled={!patternId}
              onClick={(): void => navigate(`/pattern/${patternId}?step=1`)}
            >
              <Typography sx={buttonTextIconLarge}>項目設定</Typography>
            </Button>
            <Button
              startIcon={
                loading ? (
                  <CustomLoading loading={true} />
                ) : (
                  <FileDownloadOutlined sx={styles.icon24} />
                )
              }
              sx={[secondaryButtonBackgroundColor, styles.buttonLarge]}
              disabled={loading}
              onClick={(): void => {
                if (showData.length === 0) {
                  setErrorText('出力対象のレコードがありません。');
                  setSnackbarOpen(true);
                  return;
                }
                setFileExportModalOpen(true);
              }}
            >
              <Typography sx={buttonTextIconLarge}>{fileExportButtonLabel}</Typography>
            </Button>
          </Box>
        </Box>
        <Grid2 container flexDirection={'column'} gap={'16px'} width={'100%'}>
          <Box
            sx={{
              marginLeft: '32px',
              marginRight: '32px',
              width: 'calc(100% - 64px)',
              '@media screen and (max-width:960px)': {
                marginLeft: '16px',
                marginRight: '16px',
                width: 'calc(100% - 32px)',
              },
            }}
          >
            <AiOcrSearchForm
              key={patternId}
              watchSearchItems={watchSearchItems}
              setWatchSearchItems={setWatchSearchItems}
              onClearSearch={handleClearSearch}
              customizeFields={customizeFields}
              ocrItemKeywords={ocrItemKeywords}
              onOcrItemKeywordChange={handleOcrItemKeywordChange}
              // 要注意は未処理のものだけが対象のため、他のタブでは絞り込みを出さない（#3334）
              hideNeedsReview={selectedTab !== processingStatus.processing}
              subPatterns={subPatterns}
            />
          </Box>
          <Box sx={styles.tabAndButtonWrapper}>
            <Box sx={styles.tabWrapper}>
              <TabItem status={processingStatus.processing} />
              <TabItem status={processingStatus.completed} />
              <TabItem status={processingStatus.outputted} />
            </Box>
            {/* 一括操作ボタン群（チェック選択時のみ表示）#2642
                  狭い画面でも見切れないようアイコン+件数のみ表示し、機能名はTooltip */}
            {selectedIds.size > 0 && (
              <Box sx={{ display: 'flex', gap: '8px', marginLeft: '12px' }}>
                {/* 確認済にする（#3262）→ 担当者変更 → 仕分け変更 → 削除 の順。
                    要注意以外をまとめて確認済にしたい場合は、検索の
                    「要注意以外」で絞り込んでから全選択して使う */}
                <Tooltip title="確認済にする">
                  <Button
                    onClick={(): void => setBulkCompleteDialogOpen(true)}
                    sx={[
                      secondaryButtonBackgroundColor,
                      {
                        height: '30px',
                        minWidth: 'auto',
                        padding: '4px 10px',
                        gap: '4px',
                      },
                    ]}
                  >
                    <CheckCircleOutline sx={{ fontSize: '18px' }} />
                    <Typography sx={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                      {selectedIds.size}
                    </Typography>
                  </Button>
                </Tooltip>
                <Tooltip title={assigneeLabels.changeAction}>
                  <Button
                    onClick={openBulkAssigneeDialog}
                    sx={[
                      secondaryButtonBackgroundColor,
                      {
                        height: '30px',
                        minWidth: 'auto',
                        padding: '4px 10px',
                        gap: '4px',
                      },
                    ]}
                  >
                    <PersonOutline sx={{ fontSize: '18px' }} />
                    <Typography sx={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                      ({selectedIds.size})
                    </Typography>
                  </Button>
                </Tooltip>
                {/*
                  子パターンを指定して再読み取り（#2967）。
                  子パターンを持つパターンでのみ出す。AIの判定が外れた帳票を
                  まとめて正しい子パターンで読み直すための導線
                */}
                {subPatterns.length > 0 && (
                  <Tooltip
                    title={reclassifyLoading ? '再読み取り中...' : '子パターンを指定して再読み取り'}
                  >
                    <span>
                      <Button
                        onClick={(): void => setSubPatternReOcrModalOpen(true)}
                        disabled={reclassifyLoading}
                        sx={[
                          secondaryButtonBackgroundColor,
                          {
                            height: '30px',
                            minWidth: 'auto',
                            padding: '4px 10px',
                            gap: '4px',
                          },
                        ]}
                      >
                        <ViewQuiltIcon sx={{ fontSize: '18px' }} />
                        <Typography sx={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                          ({selectedIds.size})
                        </Typography>
                      </Button>
                    </span>
                  </Tooltip>
                )}
                <Tooltip title={reclassifyLoading ? '仕分け変更中...' : '仕分け変更'}>
                  <span>
                    <Button
                      onClick={(): void => setReclassifyModalOpen(true)}
                      disabled={reclassifyLoading}
                      sx={[
                        secondaryButtonBackgroundColor,
                        {
                          height: '30px',
                          minWidth: 'auto',
                          padding: '4px 10px',
                          gap: '4px',
                        },
                      ]}
                    >
                      <SwapHoriz sx={{ fontSize: '18px' }} />
                      <Typography sx={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                        ({selectedIds.size})
                      </Typography>
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="削除">
                  <Button
                    onClick={(): void => setBulkDeleteModalOpen(true)}
                    sx={[
                      tertiaryButtonBackgroundColor,
                      {
                        height: '30px',
                        minWidth: 'auto',
                        padding: '4px 10px',
                        gap: '4px',
                      },
                    ]}
                  >
                    <DeleteOutline sx={{ fontSize: '18px' }} />
                    <Typography sx={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                      ({selectedIds.size})
                    </Typography>
                  </Button>
                </Tooltip>
              </Box>
            )}
            <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
              {customizeFields && (
                <SearchCustomizePanel
                  fields={customizeFields}
                  onUpdateField={handleUpdateCustomizeField}
                  onReset={resetCustomize}
                  onSave={handleSaveCustomize}
                  isSaving={isSavingCustomize}
                />
              )}
              <Button
                onClick={(): void =>
                  navigate(
                    `/ai-ocr/pattern/${patternId}/exports${buildResultTabQuery(selectedTab)}`,
                  )
                }
                sx={styles.headerOutlineButton}
              >
                <Typography sx={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                  ファイル出力履歴
                </Typography>
              </Button>
            </Box>
          </Box>
          <Box sx={styles.tableSection}>
            {showDataFlg === null ? (
              <CustomLoading loading={true} />
            ) : isSearchRefreshing ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <CircularProgress />
              </Box>
            ) : showDataFlg && showData.length > 0 ? (
              <Box sx={styles.tableWrapper}>
                <InfiniteScroll
                  dataLength={showData.length}
                  next={fetchMoreData}
                  hasMore={hasMore}
                  loader={<CustomLoading loading={loading} />}
                >
                  <Table sx={styles.table}>
                    <TableHead>
                      <TableRow sx={styles.tableHeadRow}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isAllSelected}
                            indeterminate={isIndeterminate}
                            onChange={handleSelectAll}
                            sx={{
                              color: gray80,
                              '&.Mui-checked': {
                                color: gray80,
                              },
                              '&.MuiCheckbox-indeterminate': {
                                color: gray80,
                              },
                            }}
                          />
                        </TableCell>
                        {/*
                          ファイル名: ホーム画面に合わせ、ファイル名(1行目)＋ステータス(2行目)を2行で表示。
                          #3134: 余った横幅を吸収して広がり、カスタマイズ表示列が増えると段階的に縮んで他の列に幅を譲る
                          （ファイル名自体は省略＋Tooltipで全文を確認できる）。
                        */}
                        <TableCell
                          sx={{
                            ...styles.tableHeadText,
                            ...fileNameColumnSx,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          ファイル名
                        </TableCell>
                        {/*
                          カスタマイズ表示列（OCR読取項目）のヘッダー。
                          #3134: minWidth 未指定だと table-layout:auto でセル値が空（"-"のみ）のとき
                          列が最小幅まで潰れ、ヘッダーの項目名が1文字ずつ縦に折り返されてしまう。
                          幅の下限を確保して潰れを防ぎつつ、項目名は判読できるよう2行までの折り返しを許可し、
                          それでも収まらない長いラベルのみ省略＋Tooltipで全文を見せる。
                        */}
                        {visibleOcrColumns.map(col => (
                          <TableCell
                            key={col.fieldName}
                            sx={{
                              ...styles.tableHeadText,
                              boxSizing: 'border-box',
                              width: `${ocrColumnWidth}px`,
                              minWidth: `${ocrColumnWidth}px`,
                              maxWidth: `${ocrColumnWidth}px`,
                            }}
                          >
                            <Tooltip title={col.label}>
                              <Box sx={styles.tableHeadDynamicLabel}>{col.label}</Box>
                            </Tooltip>
                          </TableCell>
                        ))}
                        {/* 作成者: デフォルト非表示。検索・表示設定でONにしたときのみ列を表示 */}
                        {showCreatorColumn && (
                          <TableCell
                            sx={{
                              ...styles.tableHeadText,
                              boxSizing: 'border-box',
                              width: '160px',
                              minWidth: '160px',
                              maxWidth: '160px',
                            }}
                          >
                            作成者
                          </TableCell>
                        )}
                        {/*
                          #3134: 余白の吸収はファイル名列（width:100%）が担うため、
                          従来の専用スペーサー列は不要になった。
                          担当者・アップロード日時・三点リーダーは幅固定のまま右端に寄る。
                        */}
                        {/* 担当者は常時表示。右端側に固定する（#2969 FB） */}
                        <TableCell
                          sx={{
                            ...styles.tableHeadText,
                            boxSizing: 'border-box',
                            width: '160px',
                            minWidth: '160px',
                            maxWidth: '160px',
                          }}
                        >
                          {assigneeLabels.label}
                        </TableCell>
                        {/* アップロード日時: 担当者の右に配置（#2969 レイアウト調整） */}
                        <TableCell
                          sx={{
                            ...styles.tableHeadText,
                            boxSizing: 'border-box',
                            width: '170px',
                            minWidth: '170px',
                            maxWidth: '170px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          アップロード日時
                        </TableCell>
                        <TableCell
                          sx={{
                            boxSizing: 'border-box',
                            width: '48px',
                            minWidth: '48px',
                            maxWidth: '48px',
                          }}
                        ></TableCell>
                      </TableRow>
                    </TableHead>

                    {/* ここから表示データ */}
                    <TableBody>
                      {showData.map((data, index) => {
                        // 処理中またはエラー状態の場合はクリック無効化（運営スタッフは閲覧可能）
                        const isDisabled =
                          !isOperatorStaff(user?.role) &&
                          (data.ocr_status === ocrStatus.processing ||
                            data.ocr_status === ocrStatus.error);
                        const isDeletedResult = data.status === resultStatus.deleted;

                        return (
                          <TableRow
                            key={index}
                            onClick={
                              isDisabled
                                ? undefined
                                : (): void => {
                                    const serializedSearchData = {
                                      ...watchSearchItems,
                                      startDate: watchSearchItems.startDate
                                        ? watchSearchItems.startDate.toISOString()
                                        : null,
                                      endDate: watchSearchItems.endDate
                                        ? watchSearchItems.endDate.toISOString()
                                        : null,
                                    };
                                    // 一覧の表示順を詳細画面の「次へ/前へ」に引き継ぎ、並び順を一致させる
                                    const navigationResultIds = showData.map(d => d.id);
                                    navigate(`/ai-ocr/pattern/${patternId}/result/${data.id}`, {
                                      state: {
                                        searchData: serializedSearchData,
                                        navigationResultIds,
                                        ocrItemKeywords,
                                      },
                                    });
                                  }
                            }
                            sx={{
                              ...styles.tableDataRow,
                              cursor: isDisabled ? 'default' : 'pointer',
                              '&:hover': isDisabled ? {} : styles.tableDataRow['&:hover'],
                              ...(isDeletedResult && { opacity: 0.6, backgroundColor: '#f5f5f5' }),
                            }}
                          >
                            {/* チェックボックス */}
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedIds.has(data.id)}
                                onChange={(e): void => {
                                  e.stopPropagation();
                                  handleSelectOne(data.id);
                                }}
                                onClick={(e): void => e.stopPropagation()}
                                sx={{
                                  color: gray80,
                                  '&.Mui-checked': {
                                    color: gray80,
                                  },
                                }}
                              />
                            </TableCell>
                            {/* ファイル名／読取ステータス（ホーム画面に合わせ、アイコン＋ファイル名＋ステータスの2行表示） */}
                            <TableCell sx={{ ...styles.tableDataText, ...fileNameColumnSx }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  width: '100%',
                                  minWidth: 0,
                                  overflow: 'hidden',
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '32px',
                                    height: '40px',
                                    flexShrink: 0,
                                  }}
                                >
                                  <OcrStatusIcon status={data.ocr_status} />
                                </Box>
                                <OcrFileNameStatus
                                  fileName={data.originalFiles?.[0]?.file_name || '-'}
                                  statusText={((): string => {
                                    switch (data.ocr_status) {
                                      case ocrStatus.error:
                                        return '読取エラー';
                                      case ocrStatus.completed:
                                        return '読取完了';
                                      case ocrStatus.uploaded:
                                      case ocrStatus.processing:
                                        return '読取中';
                                      default:
                                        return '';
                                    }
                                  })()}
                                  statusTooltip={
                                    data.ocr_status === ocrStatus.error ? (
                                      <>
                                        ファイルの読み取りに失敗しました。
                                        <br />
                                        再度アップロードをお願いします。
                                      </>
                                    ) : undefined
                                  }
                                  fileNameSuffix={
                                    /* 削除バッジ・要注意バッジ（#3262）。
                                       複数立つこともある。どちらも「対応が必要」を示すため
                                       ファイル名の行にまとめ、対応の要否が高いものから並べる。
                                       子パターンバッジは情報であり、ステータスの行に置く（#3314） */
                                    isDeletedResult || isNeedsReviewResult(data) ? (
                                      <>
                                        {isDeletedResult && <DeletedBadge />}
                                        {/* 件数は出さず「要注意」の印だけにする（#3328）。
                                            review_item_count は保存時点の件数で、
                                            詳細画面の判定（画面を開いた時点）とズレうるため */}
                                        {isNeedsReviewResult(data) && (
                                          <ReviewChip
                                            marginLeft={isDeletedResult ? '4px' : undefined}
                                          />
                                        )}
                                      </>
                                    ) : undefined
                                  }
                                  /* どの子パターンで読んだかを示す情報のため、
                                     警告バッジとは行を分けて読取ステータスの右に置く（#3314） */
                                  statusSuffix={
                                    <SubPatternChip subPatternName={data.sub_pattern_name} />
                                  }
                                />
                              </Box>
                            </TableCell>

                            {/* カスタマイズ表示列（OCR読取項目） */}
                            {visibleOcrColumns.map(col => {
                              const resultData = data.result_data as Record<string, unknown>;
                              const fieldId = col.parentId ?? col.fieldName;
                              const raw = resultData?.[fieldId];
                              let displayValue = '-';
                              // 日付型項目は指定された出力フォーマットで表示する（#2775）
                              const formatCellValue = (val: unknown): string => {
                                if (val == null) return '-';
                                if (
                                  col.inputType === searchFieldInputType.date &&
                                  col.outputFormat
                                ) {
                                  return formatDateValue(val, col.outputFormat) || '-';
                                }
                                // 選択肢項目は出力値ではなく表示名で見せる（#3204）。
                                // 候補外の値は resolveChoiceLabel が元の値をそのまま返す
                                if (
                                  col.inputType === searchFieldInputType.choice &&
                                  col.choices?.length
                                ) {
                                  return resolveChoiceLabel(String(val), col.choices) || '-';
                                }
                                return String(val);
                              };
                              if (Array.isArray(raw) && raw.length > 0) {
                                // テーブル型：先頭行の当該子フィールドの値を表示
                                const firstRow = raw[0] as Record<string, unknown>;
                                const cell = firstRow?.[col.fieldName] as
                                  | { value?: unknown }
                                  | unknown;
                                const val =
                                  typeof cell === 'object' &&
                                  cell !== null &&
                                  'value' in (cell as object)
                                    ? (cell as { value?: unknown }).value
                                    : cell;
                                displayValue = formatCellValue(val);
                              } else if (
                                typeof raw === 'object' &&
                                raw !== null &&
                                'value' in (raw as object)
                              ) {
                                // 単一値フィールド
                                const val = (raw as { value?: unknown }).value;
                                displayValue = formatCellValue(val);
                              }
                              return (
                                <TableCell
                                  key={col.fieldName}
                                  sx={{
                                    ...styles.tableDataText,
                                    boxSizing: 'border-box',
                                    width: `${ocrColumnWidth}px`,
                                    minWidth: `${ocrColumnWidth}px`,
                                    maxWidth: `${ocrColumnWidth}px`,
                                    overflow: 'hidden',
                                  }}
                                >
                                  <Tooltip title={displayValue}>
                                    <span
                                      style={{
                                        ...styles.mainEllipsisText,
                                        maxWidth: `${ocrColumnWidth - tableCellHorizontalPaddingTotal}px`,
                                      }}
                                    >
                                      {displayValue}
                                    </span>
                                  </Tooltip>
                                </TableCell>
                              );
                            })}

                            {/* 作成者（既存）#2642。#2969: デフォルト非表示（一覧表示ON時のみ） */}
                            {showCreatorColumn && (
                              <TableCell
                                sx={{
                                  boxSizing: 'border-box',
                                  padding: '0 16px',
                                  width: '160px',
                                  minWidth: '160px',
                                  maxWidth: '160px',
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <Avatar
                                    src={data.iconUrl || NoAccountIcon}
                                    sx={{ ...styles.avatar, flexShrink: 0 }}
                                  />
                                  <Typography sx={{ lineHeight: '24px' }}>
                                    <Tooltip title={data.showName}>
                                      <span
                                        style={{ ...styles.showFileTitleText, maxWidth: '96px' }}
                                      >
                                        {data.showName}
                                      </span>
                                    </Tooltip>
                                  </Typography>
                                </Box>
                              </TableCell>
                            )}
                            {/* #3134: 余白の吸収はファイル名列が担うためスペーサー列は廃止（ヘッダーと列数を揃える） */}
                            {/* 担当者（#2642）退会済ユーザーは「退会済ユーザー」表記。#2969 FB: 三点リーダーの左に右端固定 */}
                            <TableCell
                              sx={{
                                boxSizing: 'border-box',
                                padding: '0 16px',
                                width: '160px',
                                minWidth: '160px',
                                maxWidth: '160px',
                              }}
                            >
                              {((): ReactElement => {
                                const { displayName, displayIconUrl, isWithdrawn } =
                                  resolveAssigneeDisplay(
                                    data.assignee_id,
                                    data.assignee_name,
                                    data.assignee_icon_url,
                                    assigneeDisplayOptions,
                                  );
                                if (isWithdrawn) {
                                  return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <Typography sx={{ color: gray60 }}>
                                        <span
                                          style={{
                                            ...styles.showFileTitleText,
                                            maxWidth: '128px',
                                          }}
                                        >
                                          {displayName}
                                        </span>
                                      </Typography>
                                    </Box>
                                  );
                                }
                                return (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Avatar
                                      src={displayIconUrl || NoAccountIcon}
                                      sx={{ ...styles.avatar, flexShrink: 0 }}
                                    />
                                    <Typography sx={{ lineHeight: '24px' }}>
                                      <Tooltip title={displayName}>
                                        <span
                                          style={{ ...styles.showFileTitleText, maxWidth: '96px' }}
                                        >
                                          {displayName}
                                        </span>
                                      </Tooltip>
                                    </Typography>
                                  </Box>
                                );
                              })()}
                            </TableCell>
                            {/* アップロード日（担当者の右に配置。#2969 レイアウト調整） */}
                            <TableCell
                              sx={{
                                ...styles.tableDataText,
                                boxSizing: 'border-box',
                                width: '170px',
                                minWidth: '170px',
                                maxWidth: '170px',
                              }}
                            >
                              {formatTimestamp(data.created_at)}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...styles.tableCellButton,
                                boxSizing: 'border-box',
                                width: '48px',
                                minWidth: '48px',
                                maxWidth: '48px',
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={(event): void => {
                                  event.stopPropagation();
                                  setMenuAnchorEl(event.currentTarget);
                                  setMenuTargetId(data.id);
                                }}
                              >
                                <MoreVert />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </InfiniteScroll>
              </Box>
            ) : (
              <>
                <DescriptionIcon sx={styles.icon} />
                <Typography sx={styles.title}>{titleLabel}</Typography>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={styles.description}>{descriptionLabel}</Typography>
                </Box>
                {pattern.status !== patternStatus.deleted &&
                  selectedTab === processingStatus.processing && (
                    <Button
                      onClick={handleAddNew}
                      startIcon={<CloudUploadOutlined sx={styles.icon24} />}
                      sx={[
                        primaryButtonBackgroundColor,
                        styles.buttonLarge,
                        {
                          marginTop: '12px',
                          minWidth: '200px',
                          '@media screen and (max-width:960px)': {
                            display: 'none',
                          },
                        },
                      ]}
                    >
                      <Typography sx={buttonTextIconLarge}>新規文字読取</Typography>
                    </Button>
                  )}
              </>
            )}
          </Box>
        </Grid2>
      </Box>

      <OcrLimitDialog
        open={limitDialogOpen}
        onClose={(): void => setLimitDialogOpen(false)}
        companyPlan={company?.plan}
      />

      <CustomSnackbar
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={errorText}
        infoText={infoText}
      />

      <UploadModal
        open={uploadModalOpen}
        onClose={(): void => {
          setUploadModalOpen(false);
          setIsSubmitted(false);
        }}
        onSubmit={onSubmit}
        control={control}
        handleSubmit={handleSubmit}
        errors={errors}
        watchedImages={watchedImages}
        isSubmitted={isSubmitted}
        processingTypeValue={processingTypeValue}
        onProcessingTypeChange={handleProcessingTypeChange}
        onFilesSelected={handleFilesSelected}
        onFileDeleted={handleFilesDeleted}
        pdfPageCountCache={pdfPageCountCache}
        selectedGeminiModel={selectedGeminiModel}
        onGeminiModelChange={(newModel): void => {
          setSelectedGeminiModel(newModel);
          setThinkingBudgetValue(
            newModel === geminiModel.pro_2_5
              ? geminiThinkingBudget.minimal
              : geminiThinkingBudget.disabled,
          );
          setThinkingLevelValue(null);
        }}
        thinkingBudgetValue={thinkingBudgetValue}
        onThinkingBudgetChange={(val): void => setThinkingBudgetValue(Number(val))}
        thinkingLevelValue={thinkingLevelValue}
        onThinkingLevelChange={setThinkingLevelValue}
        ocrEngine={ocrEngine}
        onOcrEngineChange={setOcrEngine}
        geminiTemperature={geminiTemperature}
        onGeminiTemperatureChange={setGeminiTemperature}
        geminiTopP={geminiTopP}
        onGeminiTopPChange={setGeminiTopP}
        includeCoordinates={includeCoordinates}
        onIncludeCoordinatesChange={setIncludeCoordinates}
        structuredEngine={structuredEngine}
        onStructuredEngineChange={setStructuredEngine}
        canUseVerificationSettings={canUseVerificationSettings}
        isProAllowedPattern={isProAllowedPattern}
        isOcrEngineSelectablePattern={isOcrEngineSelectablePattern}
        totalPageCount={totalPageCount}
        isCalculating={isCalculating}
        baseRemainingCount={baseRemainingCount}
        user={user}
        company={company}
        fileCountOverText={fileCountOverText}
        proxyDialogSx={proxyDialogSx}
        getUserLimitMessage={getUserLimitMessage}
        showAssigneeSelect
        assigneeOptions={assigneeOptions}
        selectedAssigneeUid={selectedAssigneeUid}
        onAssigneeChange={setSelectedAssigneeUid}
      />

      <DeleteModal
        open={deleteModalOpen}
        onClose={(): void => setDeleteModalOpen(false)}
        onDelete={handleDeleteResult}
        title="ファイル"
      />

      {/* 要注意以外を確認済にする 確認ダイアログ（#3262）
          「要注意は目安であり正しさを保証しない」ことを操作の直前に示す。
          後から誤りが見つかっても、ユーザーが何を了解して押したかが明確になる */}
      <StatusChangeConfirmDialog
        open={bulkCompleteDialogOpen}
        title="確認済にする"
        body={
          `選択した ${bulkCompleteTargetIds.length} 件を確認済にします。\n\n` +
          '・要注意は読み取り信頼度・入力チェック・マスタ照合をもとにした目安であり、' +
          '内容が正しいことを保証するものではありません\n' +
          '・確認済にした後も、内容の確認・修正はできます'
        }
        confirmLabel={`${bulkCompleteTargetIds.length} 件を確認済にする`}
        processingLabel="変更中..."
        processing={bulkCompleteProcessing}
        onClose={(): void => setBulkCompleteDialogOpen(false)}
        onConfirm={handleBulkComplete}
      />

      {/* 一括削除モーダル */}
      <DeleteModal
        open={bulkDeleteModalOpen}
        onClose={(): void => setBulkDeleteModalOpen(false)}
        onDelete={handleBulkDelete}
        title={`${selectedIds.size}件のファイル`}
        loading={bulkDeleteLoading}
      />

      {/* ファイル出力モーダル */}
      <FileExportDialog
        open={fileExportModalOpen}
        onClose={(): void => {
          setFileExportModalOpen(false);
        }}
        onExport={handleFileExport}
        pattern={pattern}
        selectedTab={selectedTab}
        selectedCount={selectedIds.size}
      />

      {/*
        子パターンを指定した一括再読み取り（#2967）。
        親パターンは変えず、適用する子パターンだけを差し替えて読み直す
      */}
      <OcrCreditConsumeConfirmModal
        open={subPatternReOcrModalOpen}
        onClose={(): void => setSubPatternReOcrModalOpen(false)}
        onConfirm={(subPatternId): void => {
          if (subPatternId) handleBulkSubPatternReOcr(subPatternId);
        }}
        subPatterns={subPatterns}
      />

      {/* 仕分け変更モーダル（一括 or 個別） */}
      <PatternSelectModal
        open={reclassifyModalOpen}
        onClose={(): void => {
          setReclassifyModalOpen(false);
          setSingleReclassifyTargetId('');
        }}
        onSelect={singleReclassifyTargetId ? handleSingleReclassify : handleBulkReclassify}
        currentPatternId={patternId}
        companyId={effectiveCompanyId || ''}
        usesCredit={calculateOcrLimitInfo(company, baseRemainingCount).remainingCount <= 0}
        hideWarning={isProxyMode}
      />

      {/* 行メニュー */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={(): void => {
          setMenuAnchorEl(null);
          setMenuTargetId('');
        }}
      >
        {/* 担当者変更 → 仕分け変更 → 削除 の順（#2642） */}
        <MenuItem
          onClick={(): void => {
            const targetId = menuTargetId;
            setMenuAnchorEl(null);
            setMenuTargetId('');
            openSingleAssigneeDialog(targetId);
          }}
        >
          <ListItemIcon>
            <PersonOutline fontSize="small" />
          </ListItemIcon>
          <ListItemText>{assigneeLabels.changeAction}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={(): void => {
            setMenuAnchorEl(null);
            setSingleReclassifyTargetId(menuTargetId);
            setMenuTargetId('');
            setReclassifyModalOpen(true);
          }}
          disabled={reclassifyLoading}
        >
          <ListItemIcon>
            <SwapHoriz fontSize="small" />
          </ListItemIcon>
          <ListItemText>仕分け変更</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={(): void => {
            setMenuAnchorEl(null);
            setDeleteTargetId(menuTargetId);
            setMenuTargetId('');
            setDeleteModalOpen(true);
          }}
        >
          <ListItemIcon>
            <DeleteOutline fontSize="small" />
          </ListItemIcon>
          <ListItemText>削除</ListItemText>
        </MenuItem>
      </Menu>

      {/* 担当者変更ダイアログ（#2642）single/bulk 共通。親が保持する候補リストを渡して二重フェッチを防ぐ */}
      <AssigneeChangeDialog
        open={assigneeDialogOpen}
        onClose={(): void => setAssigneeDialogOpen(false)}
        mode={assigneeDialogMode}
        targetResultIds={assigneeDialogTargets}
        currentAssigneeId={assigneeDialogCurrent}
        targetLabel={assigneeDialogLabel}
        companyId={effectiveCompanyId ?? user?.company_id ?? ''}
        options={assigneeOptions}
        onSuccess={handleAssigneeChangeSuccess}
        onError={handleAssigneeChangeError}
      />
    </Box>
  );
};

export default PatternAiOcr;
